import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { PaymentConfirmationPreview, partyAndRowsForExpensePayment } from "./paymentConfirmation";
import DraggableModal from "./DraggableModal";
import { LOGO_B64 } from "./SalesReports";
import BulkDeleteDialog from "./BulkDeleteDialog";
import BulkStatusDialog from "./BulkStatusDialog";
import SearchSelect from "./SearchSelect";
import { defaultOpenSeasonIds, buildSeasonFilterOptions, seasonFilterPlaceholder, resolveSeasonId } from "./seasonFilter";
import useDragReorder, { DragHandle } from "./useDragReorder";
import ExportButton from "./ExportButton";
import { num2, intOr, s as xs } from "./xlsxExport";
import { loadFromSupabase, saveToSupabase, saveManyToSupabase, deleteFromSupabase } from "../supabaseDB";
import { uploadFileToStorage, removeFromStorage } from "../storageImages";
import FileDropZone from "./FileDropZone";

/* Standard docs bucket shared across the system (ShipmentRegistry,
   ShipperPortal, SweetLeafPortal). Reused here for expense bill
   attachments instead of a new bucket. */
var EXPENSE_DOCS_BUCKET = "shipment-docs";

var T = {
  en: {
    title: "Expense Bills", subtitle: "Vendor invoices for customs, warehouse, QC, and other services",
    add: "New expense bill", edit: "Edit expense bill",
    search: "Search by vendor, reference...",
    vendor: "From", reference: "Reference", date: "Date", dueDate: "Due Date",
    amount: "Amount", due: "Due", paid: "Paid", balance: "Balance",
    status: "Status", action: "Action",
    statusDraft: "Draft", statusAwaiting: "Awaiting Payment", statusPaid: "Paid", statusOverdue: "Overdue",
    addPayment: "Add Payment", paymentDate: "Payment Date", paymentAmount: "Payment Amount",
    paymentHistory: "Payments",
    processingDate: "Processing Date", deliveryDate: "Delivery Date",
    selectCategory: "Category", addCategory: "Add category",
    catCustoms: "Customs", catWarehouse: "Warehouse", catQC: "Quality/Inspection",
    catShipping: "Shipping", catRepack: "Repack", catFinancing: "Financing", catCertification: "Certification", catOther: "Other",
    entryNumber: "Entry #", notes: "Notes", selectVendor: "Select vendor",
    save: "Save", cancel: "Cancel", delete: "Delete", editBtn: "Edit",
    confirmDel: "Delete this expense bill?", yes: "Yes, delete", no: "Cancel",
    confirmExit: "Unsaved changes. Exit?", yesExit: "Yes, exit", noExit: "Keep editing",
    saved: "Expense bill saved", deleted: "Expense bill deleted",
    allCategories: "All categories", allVendors: "All vendors", allStatuses: "All statuses",
    selected: "selected", markPaid: "Mark as Paid", totalSelected: "Total selected",
    noResults: "No expense bills found",
    makePayment: "Make Payment", makePayments: "Make Payments",
    paymentsSection: "Payments", originalTotal: "Original Total", currentBalance: "Current Balance",
    noPayments: "No payments recorded", paymentRef: "Reference", paymentMethodCol: "Method",
    editLockedTitle: "Editing locked",
    editLockedMsg: "This record has payments applied. To edit, first delete the payments.",
    editLockedHint: "Click any payment below to open it in Payments and delete it.",
    batchPaymentTitle: "Batch Payment",
    paymentMethod: "Method", paymentReference: "Reference / Wire #",
    refColumn: "Reference",
    methodWire: "Wire", methodCheck: "Check", methodAch: "ACH", methodTransfer: "Transfer", methodCash: "Cash", methodOther: "Other",
    dueAmount: "Due Amount", totalPayment: "Total Payment",
    noOpenBills: "Selected bills have no outstanding balance.",
    paymentSaved: "Payment recorded",
    overApply: "Payment cannot exceed Due Amount",
    notes: "Notes",
    lines: "Line Items", addLine: "Add line", lineCat: "Category", lineDesc: "Description",
    lineAmount: "Amount", lineBill: "→Bill", lineTotal: "Total",
    sendToBill: "Send to bill", allocatedBadge: "Allocated", referenceOnly: "Reference only",
    costPlt: "Cost/plt", chargePlt: "Charge/plt", gfiCost: "GFI Cost", shipperCharge: "Shipper", lineMarkup: "Markup",
    printPreview: "Print / PDF", expenseBillPdf: "Expense Bill PDF",
    attachBill: "Attach Bill", fileUploaded: "File uploaded", replaceFile: "Replace", downloadFile: "Download", noFile: "No file",
    deleteFile: "Delete", confirmDelete: "Delete this file?", viewFile: "View",
  },
  es: {
    title: "Facturas de Gastos", subtitle: "Facturas de proveedores: aduana, bodega, QC y otros servicios",
    add: "Nueva factura de gasto", edit: "Editar factura de gasto",
    search: "Buscar por proveedor, referencia...",
    vendor: "De", reference: "Referencia", date: "Fecha", dueDate: "Fecha de Pago",
    amount: "Monto", due: "Por Pagar", paid: "Pagado", balance: "Saldo",
    status: "Estado", action: "Acción",
    statusDraft: "Borrador", statusAwaiting: "Por Pagar", statusPaid: "Pagado", statusOverdue: "Vencido",
    addPayment: "Registrar Pago", paymentDate: "Fecha de Pago", paymentAmount: "Monto del Pago",
    paymentHistory: "Pagos",
    processingDate: "Fecha de Procesamiento", deliveryDate: "Fecha de Entrega",
    selectCategory: "Categoría", addCategory: "Agregar categoría",
    catCustoms: "Aduana", catWarehouse: "Bodega", catQC: "Calidad/Inspección",
    catShipping: "Envío", catRepack: "Repack", catFinancing: "Financiamiento", catCertification: "Certificación", catOther: "Otro",
    entryNumber: "Entry #", notes: "Notas", selectVendor: "Seleccionar proveedor",
    save: "Guardar", cancel: "Cancelar", delete: "Eliminar", editBtn: "Editar",
    confirmDel: "¿Eliminar esta factura?", yes: "Sí, eliminar", no: "Cancelar",
    confirmExit: "Cambios sin guardar. ¿Salir?", yesExit: "Sí, salir", noExit: "Seguir editando",
    saved: "Factura guardada", deleted: "Factura eliminada",
    allCategories: "Todas", allVendors: "Todos", allStatuses: "Todos",
    selected: "seleccionados", markPaid: "Marcar Pagado", totalSelected: "Total seleccionados",
    noResults: "Sin facturas de gastos",
    makePayment: "Registrar Pago", makePayments: "Registrar Pagos",
    paymentsSection: "Pagos", originalTotal: "Total Original", currentBalance: "Saldo Actual",
    noPayments: "Sin pagos registrados", paymentRef: "Referencia", paymentMethodCol: "Método",
    editLockedTitle: "Edición bloqueada",
    editLockedMsg: "Este registro tiene pagos aplicados. Para editarlo, primero elimina los pagos.",
    editLockedHint: "Click en un pago para abrirlo en Pagos y eliminarlo.",
    batchPaymentTitle: "Pago en Lote",
    paymentMethod: "Método", paymentReference: "Referencia / # Transferencia",
    refColumn: "Referencia",
    methodWire: "Transferencia", methodCheck: "Cheque", methodAch: "ACH", methodTransfer: "Transferencia", methodCash: "Efectivo", methodOther: "Otro",
    dueAmount: "Por Pagar", totalPayment: "Total del Pago",
    noOpenBills: "Las facturas seleccionadas no tienen saldo pendiente.",
    paymentSaved: "Pago registrado",
    overApply: "El pago no puede exceder el saldo pendiente",
    notes: "Notas",
    lines: "Líneas de Detalle", addLine: "Agregar línea", lineCat: "Categoría", lineDesc: "Descripción",
    lineAmount: "Monto", lineBill: "→Bill", lineTotal: "Total",
    sendToBill: "Enviar al bill", allocatedBadge: "Alocado", referenceOnly: "Solo referencia",
    costPlt: "Costo/plt", chargePlt: "Cobro/plt", gfiCost: "Costo GFI", shipperCharge: "Exportador", lineMarkup: "Markup",
    printPreview: "Imprimir / PDF", expenseBillPdf: "Expense Bill PDF",
    attachBill: "Adjuntar Bill", fileUploaded: "Archivo cargado", replaceFile: "Reemplazar", downloadFile: "Descargar", noFile: "Sin archivo",
    deleteFile: "Eliminar", confirmDelete: "¿Eliminar este archivo?", viewFile: "Ver",
  },
};

var STORAGE_KEY = "gfi-expense-bills";
var CAT_STORAGE = "gfi-expense-categories";
var DEFAULT_CATS = ["customs", "warehouse", "qc", "shipping", "repack", "financing", "certification", "other"];
var catKeys = { customs: "catCustoms", warehouse: "catWarehouse", qc: "catQC", shipping: "catShipping", repack: "catRepack", financing: "catFinancing", certification: "catCertification", other: "catOther" };
var stKeys = { draft: "statusDraft", awaiting: "statusAwaiting", paid: "statusPaid" };
var STATUS_STYLES = {
  draft: { bg: "rgba(107,114,128,0.10)", text: "#6B7280", border: "rgba(107,114,128,0.25)" },
  awaiting: { bg: "rgba(59,130,246,0.10)", text: "#2563EB", border: "rgba(59,130,246,0.25)" },
  paid: { bg: "rgba(16,185,129,0.10)", text: "#059669", border: "rgba(16,185,129,0.25)" },
  overdue: { bg: "rgba(239,68,68,0.10)", text: "#DC2626", border: "rgba(239,68,68,0.25)" },
};
/* 2026-10-21, Arturo: "matea toda la app" — same rectangular/3D-bevel
   recipe as ShipmentRegistry.js's TEXAS_QC_BEVEL, applied to every
   STATUS_STYLES badge in this file so Bill status pills read the
   same squared, beveled way everywhere in the app. */
var STATUS_BEVEL = "inset 0 1px 0 rgba(255,255,255,0.6), inset 0 -1px 2px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.12)";
var inp = { padding: "7px 8px", fontSize: 12, border: "1px solid var(--color-border)", borderRadius: 5, background: "var(--color-bg-input)", color: "var(--color-text)", outline: "none", fontFamily: "inherit", width: "100%", boxSizing: "border-box" };
/* Brushed Steel "recessed field on a plate" treatment — duplicated
   per-file from ShipmentRegistry.js's filterFieldPlate, same
   convention as BillRegistry.js/InvoiceRegistry.js (never a shared
   import). Passed to every SearchSelect/date-input inside the
   Expense Bills Filters popover. */
var filterFieldPlate = {
  background: "linear-gradient(180deg, #f2f3f5 0%, #d7dadd 100%)",
  border: "1px solid #9aa0a8",
  borderRadius: 0,
  boxShadow: "inset 0 1px 2px rgba(0,0,0,0.15), inset 0 -1px 0 rgba(255,255,255,0.6)",
  minHeight: 25,
};

function loadLS(k, fb) { try { var d = localStorage.getItem(k); return d ? JSON.parse(d) : fb; } catch { return fb; } }
function parseTermDays(term) {
  if (term === undefined || term === null || term === "") return null;
  var m = String(term).match(/(\d+)/);
  return m ? parseInt(m[1], 10) : null;
}
function addDaysToISO(isoDate, days) {
  if (!isoDate) return "";
  var d = new Date(isoDate + "T00:00:00");
  if (isNaN(d.getTime())) return "";
  d.setDate(d.getDate() + (parseInt(days, 10) || 0));
  return d.toISOString().substring(0, 10);
}
/* Resolves a vendor's payment term by name across all candidate stores
   (suppliers/clients/warehouses/brokers). Returns parsed days or null. */
function findVendorTermDays(vendorName) {
  if (!vendorName) return null;
  var target = String(vendorName).toLowerCase().trim();
  var keys = ["gfi-suppliers", "gfi-clients", "gfi-warehouses", "gfi-brokers", "gfi-quality-control", "gfi-certifiers"];
  for (var i = 0; i < keys.length; i++) {
    var list = loadLS(keys[i], []);
    for (var j = 0; j < list.length; j++) {
      var item = list[j];
      var n = (item.shortName || item.fullName || item.name || item.companyName || "").toLowerCase().trim();
      if (n === target) {
        var d = parseTermDays(item.paymentTerm || item.term);
        if (d !== null) return d;
      }
    }
  }
  return null;
}
function saveLS(k, v) { try { localStorage.setItem(k, JSON.stringify(v)); return true; } catch (e) { console.error("[ExpenseBills] storage error", k, e); return false; } }
function nextPaymentId(items) { var nums = (items || []).map(function (i) { return parseInt((i.id || "").replace("PAY-", ""), 10); }).filter(function (n) { return !isNaN(n); }); return "PAY-" + String(Math.max(0, ...nums) + 1).padStart(4, "0"); }
function todayISO() { return new Date().toISOString().substring(0, 10); }
var PAYMENT_METHODS = ["wire", "check", "ach", "transfer", "cash", "other"];
function fmtMoney(n) { var v = parseFloat(n) || 0; return "$" + v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
function fmtDate(d) { if (!d) return "\u2014"; return new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" }); }
function fmtFileSize(n) { var v = parseFloat(n) || 0; if (v < 1024) return v + " B"; if (v < 1024 * 1024) return (v / 1024).toFixed(1) + " KB"; return (v / (1024 * 1024)).toFixed(1) + " MB"; }
function nextId(items) { var nums = items.map(function(i) { return parseInt((i.id || "").replace("EXP-", ""), 10); }).filter(function(n) { return !isNaN(n); }); return "EXP-" + String((Math.max(0, ...nums) + 1)).padStart(4, "0"); }
function getShipments() { return loadLS("gfi-shipments", []); }
function getBills() { return loadLS("gfi-bills", []); }

function calcBal(bill) { var total = parseFloat(bill.amount) || 0; var paid = (bill.payments || []).reduce(function(s, p) { return s + (parseFloat(p.amount) || 0); }, 0); return { total: total, paid: paid, balance: total - paid }; }
/* Status is normally computed from balance state, but a stored
   `status` field on the record wins when present. This lets the bulk
   Change Status action move records to a chosen status without
   manipulating their payments array. The form's manual edits still
   write to the same field. Internal value "awaiting" maps to the user-
   facing "Pending"/"Awaiting Payment" label. */
/* 2026-09-15 — Arturo: "la tabla de expense bills tiene tarjetas en la
   parte superior pero ninguna filtra las facturas que estaban
   pendientes de pago, el filtro de status tampoco muestra los
   pendientes de pagar". Root cause found querying production data
   directly: EVERY real expense_bills row has status="open" — a value
   this module has never written and doesn't recognize (only
   draft/awaiting/paid are ever set, via the Change Status bulk action
   below) — almost certainly left over from an old import/migration.
   The old `if (bill.status) return bill.status` trusted it blindly,
   so every single bill fell through the cracks: "open" matches none
   of draft/awaiting/overdue/paid, so no card and no filter option ever
   counted or matched it — confirmed live: EXP-0006/EXP-0007 are fully
   paid (balance $0) yet still stuck reading "open" forever, since nothing
   ever re-derives it once a stored value exists. Fix: only trust a
   stored status when it's one of the 3 values this module actually
   writes (so the real, intentional manual "Change Status" override
   still works) — anything else, including "open", falls through to
   live computation same as if nothing were stored. Also drops the old
   separate "overdue" bucket per Arturo ("no se como esten las que no
   esten pagadas porque overdue no es") — merged into "awaiting"
   (Arturo doesn't want a due-date distinction, just one pending
   bucket). */
var VALID_EXPENSE_STATUSES = { draft: true, awaiting: true, paid: true };
function getStatus(bill) {
  if (bill && bill.status && VALID_EXPENSE_STATUSES[bill.status]) return bill.status;
  var b = calcBal(bill);
  if (b.balance <= 0 && b.paid > 0) return "paid";
  if (b.total > 0) return "awaiting";
  return "draft";
}
function fmtDateLongExp(d) { if (!d) return ""; return new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }); }
function fmtMoneyNoSignExp(n) { var v = parseFloat(n) || 0; return v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
var CAT_LABELS = { customs: "Customs", warehouse: "Warehouse", qc: "Quality / Inspection", shipping: "Shipping", repack: "Repack", financing: "Financing", other: "Other" };

/* Builds printable HTML for one expense bill. Navy #1B3A4B branding to match
   Sales Reports and Invoices. Internal vendor-bill receipt format. */
export function buildExpenseBillPrintHTML(bill) {
  var bal = calcBal(bill);
  var st = getStatus(bill);
  var stLabel = st === "paid" ? "PAID" : st === "overdue" ? "OVERDUE" : st === "awaiting" ? "AWAITING PAYMENT" : "DRAFT";
  var stColor = st === "paid" ? "#059669" : st === "overdue" ? "#DC2626" : st === "awaiting" ? "#2563EB" : "#6B7280";
  var catLabel = CAT_LABELS[bill.category] || (bill.category || "");

  var h = '<!DOCTYPE html><html><head><meta charset="utf-8"><title>Expense Bill ' + (bill.id || "") + '</title><style>';
  h += '@page{size:letter;margin:0.6in 0.7in}';
  h += '*{margin:0;padding:0;box-sizing:border-box}';
  h += 'body{font-family:"Segoe UI",Arial,sans-serif;font-size:10px;color:#1a1a1a;line-height:1.4}';
  h += '.page{max-width:700px;margin:0 auto}';
  h += '.header{display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:16px;border-bottom:3px solid #1B3A4B}';
  /* Bumped to 100 — matches other GFI docs so "International" reads. */
  h += '.logo img{height:100px}';
  h += '.doc-title{font-size:26px;font-weight:300;color:#1B3A4B;letter-spacing:2px;text-align:right}';
  h += '.doc-sub{font-size:9px;text-transform:uppercase;letter-spacing:2px;color:#888;text-align:right;margin-top:4px}';
  h += '.meta-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin:14px 0;padding:12px 16px;background:#f7f8fa;border-radius:6px;border-left:4px solid #1B3A4B}';
  h += '.meta-grid .label{font-size:8px;text-transform:uppercase;letter-spacing:1px;color:#888;font-weight:700}';
  h += '.meta-grid .value{font-size:11px;font-weight:600;color:#1a1a1a;margin-top:1px}';
  h += '.vendor{margin:10px 0 14px;padding:10px 16px;border:1px solid #e0e0e0;border-radius:6px}';
  h += '.vendor .v-label{font-size:8px;text-transform:uppercase;letter-spacing:1px;color:#888;font-weight:700;margin-bottom:4px}';
  h += '.vendor .v-name{font-size:13px;font-weight:700;color:#1B3A4B}';
  h += '.status-badge{display:inline-block;padding:3px 10px;border-radius:10px;font-size:9px;font-weight:700;letter-spacing:1px;color:#fff}';
  h += 'table{width:100%;border-collapse:collapse}';
  h += '.items th{padding:7px 8px;font-size:8px;text-transform:uppercase;letter-spacing:1px;color:#fff;background:#1B3A4B;font-weight:600}';
  h += '.items th:first-child{border-radius:4px 0 0 0}.items th:last-child{border-radius:0 4px 0 0}';
  h += '.items td{padding:6px 8px;border-bottom:1px solid #eee;font-size:10px;vertical-align:middle}';
  h += '.items tr:nth-child(even){background:#fafbfc}';
  h += '.r{text-align:right}.c{text-align:center}.mono{font-family:"Courier New",monospace;font-size:10px}';
  h += '.summary{margin-top:10px;display:flex;justify-content:flex-end}';
  h += '.summary-tbl{width:380px}';
  h += '.summary-tbl td{padding:5px 8px;font-size:10px}';
  h += '.summary-tbl .total td{font-size:13px;font-weight:700;color:#1B3A4B;padding-top:8px}';
  h += '.summary-tbl .due td{font-size:14px;font-weight:800;color:#1B3A4B;border-top:3px solid #1B3A4B;padding-top:10px}';
  h += '.pay-section{margin-top:18px;padding-top:10px;border-top:1px solid #ddd}';
  h += '.pay-section h4{font-size:10px;text-transform:uppercase;letter-spacing:1px;color:#1B3A4B;font-weight:700;margin-bottom:6px}';
  h += '.pay-table th{padding:5px 8px;font-size:8px;text-transform:uppercase;color:#888;border-bottom:1px solid #ddd;text-align:left}';
  h += '.pay-table td{padding:5px 8px;font-size:9px;border-bottom:1px solid #f0f0f0}';
  h += '.footer{margin-top:20px;padding-top:10px;border-top:1px solid #ddd;font-size:8px;color:#999;text-align:center}';
  h += '</style></head><body><div class="page">';

  /* Header */
  h += '<div class="header"><div class="logo"><img src="' + LOGO_B64 + '" /></div>';
  h += '<div><div class="doc-title">EXPENSE BILL</div><div class="doc-sub">Internal Vendor Bill</div></div></div>';

  /* Meta grid */
  h += '<div class="meta-grid">';
  h += '<div><div class="label">Bill #</div><div class="value">' + (bill.id || "—") + '</div></div>';
  h += '<div><div class="label">Date</div><div class="value">' + fmtDateLongExp(bill.date) + '</div></div>';
  if (bill.reference) h += '<div><div class="label">Reference</div><div class="value">' + bill.reference + '</div></div>';
  if (catLabel) h += '<div><div class="label">Category</div><div class="value">' + catLabel + '</div></div>';
  if (bill.dueDate) h += '<div><div class="label">Due Date</div><div class="value">' + fmtDateLongExp(bill.dueDate) + '</div></div>';
  if (bill.entryNumber) h += '<div><div class="label">Entry #</div><div class="value">' + bill.entryNumber + '</div></div>';
  h += '<div><div class="label">Status</div><div class="value"><span class="status-badge" style="background:' + stColor + '">' + stLabel + '</span></div></div>';
  h += '</div>';

  /* Vendor */
  h += '<div class="vendor"><div class="v-label">Vendor</div>';
  h += '<div class="v-name">' + (bill.vendor || "—") + '</div>';
  h += '</div>';

  /* Line items: ExpenseBill stores rows in bill.fees (each: feeType, feeCustom,
     qty, rate, amount, shipmentId, billId, allocateToBill). shipmentId/lot is
     internal bookkeeping only (feeds ProfitTracker's per-lot cost
     distribution) — the bill referenced by billId is what shows here, with
     a badge for whether it was actually allocated to that bill or is just
     a reference. */
  var fees = (bill.fees || []).filter(function(fe) { return (parseFloat(fe.amount) || 0) !== 0 || fe.feeType || fe.feeCustom; });
  if (fees.length > 0) {
    h += '<table class="items"><thead><tr>';
    h += '<th style="text-align:left;width:40%">Description</th>';
    h += '<th class="r" style="width:10%">Qty</th>';
    h += '<th class="r" style="width:15%">Rate</th>';
    h += '<th class="r" style="width:15%">Amount</th>';
    h += '<th class="c" style="width:20%">Bill Ref</th>';
    h += '</tr></thead><tbody>';
    /* 2026-10-16 — a fee can now be split across several target bills
       (fe.allocations); print each as its own small line with its
       real LOT # (not just the raw billId, the pre-existing single-
       bill version never even resolved that) and its own
       Allocated/Reference badge. This function only receives `bill`,
       not the full bills list — read gfi-bills straight from
       localStorage like every other print/export helper in this app
       does for cross-references. */
    var allBillsForPrint = loadLS("gfi-bills", []);
    fees.forEach(function(fe) {
      var name = fe.feeType === "__custom__" ? (fe.feeCustom || "Fee") : (fe.feeType || "Fee");
      var qty = fe.qty || (fe.pctRate ? fe.pctRate + "%" : "—");
      var rate = parseFloat(fe.rate) || 0;
      var amt = parseFloat(fe.amount) || 0;
      var allocs = (fe.allocations && fe.allocations.length > 0)
        ? fe.allocations.filter(function(al) { return al.billId; })
        : (fe.billId ? [{ billId: fe.billId, amount: fe.amount, allocate: !!fe.allocateToBill }] : []);
      var refCell = allocs.length === 0 ? "—" : allocs.map(function(al) {
        var b2 = allBillsForPrint.find(function(bb) { return bb.id === al.billId; });
        var lotLabel = "LOT " + (b2 ? b2.lot : "—") + " · $" + fmtMoneyNoSignExp(parseFloat(al.amount) || 0);
        var badge = al.allocate
          ? '<span style="display:inline-block;margin-top:1px;padding:1px 6px;border-radius:8px;font-size:7px;font-weight:700;text-transform:uppercase;background:#E8F5E9;color:#2E7D32">Allocated</span>'
          : '<span style="display:inline-block;margin-top:1px;padding:1px 6px;border-radius:8px;font-size:7px;font-weight:700;text-transform:uppercase;background:#F1F1F1;color:#777">Reference only</span>';
        return '<div style="margin-bottom:2px">' + lotLabel + '<br/>' + badge + '</div>';
      }).join('');
      h += '<tr>';
      h += '<td><b>' + name + '</b></td>';
      h += '<td class="r mono">' + qty + '</td>';
      h += '<td class="r mono">' + (rate > 0 ? "$" + fmtMoneyNoSignExp(rate) : "—") + '</td>';
      h += '<td class="r mono">$' + fmtMoneyNoSignExp(amt) + '</td>';
      h += '<td class="c mono" style="font-size:8px;color:#888">' + refCell + '</td>';
      h += '</tr>';
    });
    h += '</tbody></table>';
  }

  /* Notes */
  if (bill.notes) {
    h += '<div style="margin:10px 0;padding:10px 14px;background:#fafbfc;border-radius:6px;font-size:10px;color:#555">';
    h += '<div style="font-size:8px;text-transform:uppercase;letter-spacing:1px;color:#888;font-weight:700;margin-bottom:4px">Notes</div>';
    h += bill.notes.replace(/\n/g, "<br>");
    h += '</div>';
  }

  /* Summary */
  h += '<div class="summary"><table class="summary-tbl">';
  h += '<tr class="total"><td class="r"><b>Total Amount</b></td><td class="r mono"><b>$' + fmtMoneyNoSignExp(bal.total) + '</b></td></tr>';
  if (bal.paid > 0) h += '<tr><td class="r">Less: Paid</td><td class="r mono">($' + fmtMoneyNoSignExp(bal.paid) + ')</td></tr>';
  h += '<tr class="due"><td class="r">BALANCE DUE</td><td class="r mono">$' + fmtMoneyNoSignExp(bal.balance) + '</td></tr>';
  h += '</table></div>';

  /* Payment history */
  if ((bill.payments || []).length > 0) {
    h += '<div class="pay-section"><h4>Payment History</h4><table class="pay-table"><thead><tr>';
    h += '<th>Pay#</th><th>Date</th><th>Method</th><th>Reference</th><th class="r">Amount</th>';
    h += '</tr></thead><tbody>';
    bill.payments.forEach(function(p) {
      h += '<tr><td class="mono">' + (p.paymentId || "—") + '</td><td>' + fmtDate(p.date) + '</td><td>' + (p.method || "—") + '</td><td class="mono">' + (p.reference || "—") + '</td><td class="r mono">$' + fmtMoneyNoSignExp(p.amount) + '</td></tr>';
    });
    h += '</tbody></table></div>';
  }

  h += '<div class="footer">Ground Fresh International, Inc. · Lindenhurst, NY · ground-fresh.com</div>';
  h += '</div></body></html>';
  return h;
}

/* Multi-expense-bill print. Same shared-style + page-break pattern as
   printMultipleSalesReports and printMultipleInvoices. */
export function printMultipleExpenseBills(bills, onError) {
  if (!Array.isArray(bills) || bills.length === 0) return;
  var htmls = bills.map(function(b) { return buildExpenseBillPrintHTML(b); });
  var styleMatch = htmls[0].match(/<style>([\s\S]*?)<\/style>/);
  var sharedCss = styleMatch ? styleMatch[1] : "";
  var bodies = htmls.map(function(html) {
    var m = html.match(/<body[^>]*>([\s\S]*?)<\/body>/);
    return m ? m[1] : "";
  });
  var today = new Date().toISOString().substring(0, 10);
  var doc = '<!DOCTYPE html><html><head><meta charset="utf-8"><title>Expense Bills - ' + today + '</title><style>' + sharedCss + ' .exp-pagebreak{page-break-before:always;break-before:page;height:0;margin:0;padding:0}</style></head><body>';
  bodies.forEach(function(body, i) {
    if (i > 0) doc += '<div class="exp-pagebreak"></div>';
    doc += body;
  });
  doc += '</body></html>';
  var w = window.open("", "_blank");
  if (!w) { if (onError) onError("popup-blocked"); return; }
  w.document.open();
  w.document.write(doc);
  w.document.close();
  setTimeout(function() { try { w.focus(); w.print(); } catch(e) {} }, 600);
}

function Toast({ message, onClose }) {
  var [fading, setFading] = useState(false);
  useEffect(function() {
    var fadeT = setTimeout(function() { setFading(true); }, 2700);
    var closeT = setTimeout(function() { onClose(); }, 3000);
    return function() { clearTimeout(fadeT); clearTimeout(closeT); };
  }, [message]);
  return createPortal(<div style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", background: "#1a2636", color: "#fff", padding: "10px 24px", borderRadius: 10, fontSize: 13, fontWeight: 600, zIndex: 999999, animation: fading ? "fadeOut 0.3s ease forwards" : undefined }}>{message}</div>, document.body);
}

/* Shared SearchSelect imported from ./SearchSelect (project-wide). */


/* ======================== EXPENSE FORM ======================== */
/* Shared payments-history block. Each row navigates to PaymentRegistry. */
function goToPayment(paymentId) {
  if (window.__gfiNavigate) window.__gfiNavigate("payments");
  setTimeout(function() {
    if (typeof window.__gfiPaymentOpen === "function") window.__gfiPaymentOpen(paymentId);
  }, 220);
}

function PaymentsSection({ payments, total, t }) {
  var paidSum = (payments || []).reduce(function(s, p) { return s + (parseFloat(p.amount) || 0); }, 0);
  var balance = total - paidSum;
  var hasPayments = (payments || []).length > 0;
  return (
    <div style={{ marginTop: 18, padding: 14, borderRadius: 10, background: "var(--color-bg)", border: "1px solid var(--color-border)" }}>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--color-accent)", marginBottom: 10, paddingBottom: 4, borderBottom: "1px solid var(--color-border)" }}>{t.paymentsSection}</div>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: 12 }}>
        <span style={{ color: "var(--color-text-muted)", fontWeight: 600 }}>{t.originalTotal}</span>
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "var(--color-text)" }}>{fmtMoney(total)}</span>
      </div>
      {!hasPayments ? (
        <div style={{ padding: "10px 0", fontSize: 12, color: "var(--color-text-muted)", fontStyle: "italic", textAlign: "center" }}>{t.noPayments}</div>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11, marginTop: 6 }}>
          <thead><tr style={{ borderBottom: "1px solid var(--color-border)" }}>
            <th style={{ padding: "5px 6px", textAlign: "left", fontSize: 9, fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase" }}>PAY#</th>
            <th style={{ padding: "5px 6px", textAlign: "left", fontSize: 9, fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase" }}>Date</th>
            <th style={{ padding: "5px 6px", textAlign: "left", fontSize: 9, fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase" }}>{t.paymentMethodCol}</th>
            <th style={{ padding: "5px 6px", textAlign: "left", fontSize: 9, fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase" }}>{t.paymentRef}</th>
            <th style={{ padding: "5px 6px", textAlign: "right", fontSize: 9, fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase" }}>Amount</th>
          </tr></thead>
          <tbody>{(payments || []).map(function(p, i) {
            return (
              <tr key={i}
                  onClick={function() { goToPayment(p.paymentId); }}
                  title={"Open " + (p.paymentId || "")}
                  style={{ borderBottom: "1px solid rgba(0,0,0,0.04)", cursor: "pointer", transition: "background 0.12s" }}
                  onMouseEnter={function(e) { e.currentTarget.style.background = "rgba(42,122,111,0.06)"; }}
                  onMouseLeave={function(e) { e.currentTarget.style.background = "transparent"; }}>
                <td style={{ padding: "6px", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "var(--color-accent)" }}>{p.paymentId || "—"}</td>
                <td style={{ padding: "6px", color: "var(--color-text-secondary)" }}>{fmtDate(p.date)}</td>
                <td style={{ padding: "6px", color: "var(--color-text-secondary)", textTransform: "capitalize" }}>{p.method || "—"}</td>
                <td style={{ padding: "6px", fontFamily: "'JetBrains Mono', monospace", color: "var(--color-text-secondary)" }}>{p.reference || "—"}</td>
                <td style={{ padding: "6px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "#059669" }}>{fmtMoney(p.amount)}</td>
              </tr>
            );
          })}</tbody>
        </table>
      )}
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 10, paddingTop: 8, borderTop: "2px solid var(--color-accent)", fontSize: 13, fontWeight: 700 }}>
        <span style={{ color: "var(--color-text)" }}>{t.currentBalance}</span>
        <span style={{ fontFamily: "'JetBrains Mono', monospace", color: balance > 0.01 ? "#DC2626" : "#059669" }}>{fmtMoney(balance)}</span>
      </div>
    </div>
  );
}

/* Locks edit when an expense bill has payments applied. */
function EditLockDialog({ record, t, onClose }) {
  var payments = record.payments || [];
  return createPortal(
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1400, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }} onClick={onClose}>
      <div onClick={function(e) { e.stopPropagation(); }} style={{ background: "#fff", borderRadius: 10, maxWidth: 520, width: "100%", padding: 24, boxShadow: "0 16px 48px rgba(0,0,0,0.25)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
          <span style={{ fontSize: 22 }}>{"\u{1F512}"}</span>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#B45309" }}>{t.editLockedTitle}</h3>
        </div>
        <p style={{ margin: "0 0 6px", fontSize: 13, color: "var(--color-text)", lineHeight: 1.5 }}>{t.editLockedMsg}</p>
        <p style={{ margin: "0 0 14px", fontSize: 12, color: "var(--color-text-muted)", fontStyle: "italic" }}>{t.editLockedHint}</p>
        <div style={{ borderRadius: 8, border: "1px solid var(--color-border)", overflow: "hidden", marginBottom: 16 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead><tr style={{ background: "var(--color-bg)", borderBottom: "1px solid var(--color-border)" }}>
              <th style={{ padding: "6px 10px", textAlign: "left", fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "var(--color-text-secondary)" }}>PAY#</th>
              <th style={{ padding: "6px 10px", textAlign: "left", fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "var(--color-text-secondary)" }}>Date</th>
              <th style={{ padding: "6px 10px", textAlign: "right", fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "var(--color-text-secondary)" }}>Amount</th>
            </tr></thead>
            <tbody>{payments.map(function(p, i) {
              return (
                <tr key={i}
                    onClick={function() { goToPayment(p.paymentId); onClose(); }}
                    title={"Open " + (p.paymentId || "")}
                    style={{ borderBottom: "1px solid rgba(0,0,0,0.04)", cursor: "pointer", transition: "background 0.12s" }}
                    onMouseEnter={function(e) { e.currentTarget.style.background = "rgba(42,122,111,0.06)"; }}
                    onMouseLeave={function(e) { e.currentTarget.style.background = "transparent"; }}>
                  <td style={{ padding: "8px 10px", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "var(--color-accent)" }}>{p.paymentId || "—"} →</td>
                  <td style={{ padding: "8px 10px", color: "var(--color-text-secondary)" }}>{fmtDate(p.date)}</td>
                  <td style={{ padding: "8px 10px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "#059669" }}>{fmtMoney(p.amount)}</td>
                </tr>
              );
            })}</tbody>
          </table>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ padding: "8px 18px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>{t.cancel}</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function ExpenseForm({ bill, bills, t, lang, onSave, onDelete, onCancel, embedded }) {
  var isNew = !bill || !bill.id;
  var shipments = useMemo(function() { return getShipments().filter(function(s) { return s.status === "received" || s.status === "billed" || s.status === "draft"; }); }, []);
  /* availableBills used to be a one-shot loadLS("gfi-bills") snapshot —
     a passive mirror that only BillRegistry.js keeps fresh (it
     dual-writes the full Supabase result there on its own mount). If
     this form opens before Bills has been opened in the session, the
     mirror can be missing recently-created bills entirely (same root
     cause as the gfi-fee-types/Fee Catalog bug fixed earlier). Fix:
     load straight from Supabase here too, with the stale mirror only
     as an instant fallback so the dropdown isn't empty for the first
     render while the fetch resolves. */
  var [availableBills, setAvailableBills] = useState(function() { return getBills(); });
  useEffect(function() {
    var alive = true;
    loadFromSupabase("bills", { force: true })
      .then(function(rows) {
        if (!alive) return;
        var fresh = rows || [];
        setAvailableBills(fresh);
        /* Dual-write back to the shared mirror so other passive
           consumers of gfi-bills (including this same picker on its
           next mount) see the fresh set too — mirrors BillRegistry's
           own writeBillsCache pattern. */
        try { localStorage.setItem("gfi-bills", JSON.stringify(fresh)); } catch (e) {}
      })
      .catch(function () { /* Supabase fetch failed — keep the localStorage fallback already in state */ });
    return function () { alive = false; };
  }, []);
  /* Descending by bill date (most recent first), tiebreak by lot
     number descending — so older lots like 58044 are still reachable
     further down instead of getting lost among undated/equal-date
     rows. */
  var sortedAvailableBills = useMemo(function() {
    return availableBills.slice().sort(function(a, b) {
      var ad = a.billDate || "", bd = b.billDate || "";
      if (ad !== bd) return bd.localeCompare(ad);
      return (parseInt(b.lot, 10) || 0) - (parseInt(a.lot, 10) || 0);
    });
  }, [availableBills]);
  var existingVendors = useMemo(function() { return [...new Set(bills.map(function(b) { return b.vendor; }).filter(Boolean))].sort(); }, [bills]);
  /* Vendor catalogs (suppliers/clients/warehouses/brokers) live in
     Supabase; allVendorOptions/findVendorTermDays read them off
     localStorage["gfi-*"] for zero-latency sync access, but nothing
     guarantees those keys are populated by the time THIS form opens —
     they're only ever dual-written by each catalog's OWN registry
     screen (CustomsBrokerRegistry.js, WarehouseRegistry.js, etc.), so
     a user who opens Expense Bills without ever having visited Brokers
     in this browser session sees an empty "gfi-brokers" regardless of
     what's actually saved in Supabase (confirmed: Godinez exists there
     with payment_term "21"). Fetch all 4 straight from Supabase on
     mount, dual-write them to localStorage the same as each catalog's
     own registry does, and bump catalogVersion to force a re-render —
     allVendorOptions below is recomputed every render already, so it
     picks the fresh data up immediately once this resolves. */
  var [catalogVersion, setCatalogVersion] = useState(0);
  useEffect(function() {
    var alive = true;
    var pairs = [["suppliers", "gfi-suppliers"], ["clients", "gfi-clients"], ["warehouses", "gfi-warehouses"], ["brokers", "gfi-brokers"], ["quality_control", "gfi-quality-control"], ["certifiers", "gfi-certifiers"]];
    Promise.all(pairs.map(function(p) {
      return loadFromSupabase(p[0]).then(function(rows) {
        try { localStorage.setItem(p[1], JSON.stringify(rows)); } catch (e) {}
        return true;
      }).catch(function() { return false; });
    /* Same root cause as the vendor catalogs above, reported directly by
       Arturo from his phone: the Fee Type dropdown on each line reads
       localStorage["gfi-fee-types"], which is only dual-written by
       FeeCatalog.js's own screen — a phone/tablet that never opened Fee
       Catalog in that session saw an empty dropdown regardless of what
       was actually saved in Supabase. Fetch fee_catalog fresh here too,
       same syncFeeTypes(active-only, name list) logic FeeCatalog.js
       itself uses, and fold it into the same catalogVersion bump. */
    }).concat(loadFromSupabase("fee_catalog").then(function(rows) {
      var names = (rows || []).filter(function(f) { return f.isActive !== false; }).map(function(f) { return f.name; });
      try { localStorage.setItem("gfi-fee-types", JSON.stringify(names)); } catch (e) {}
      return true;
    }).catch(function() { return false; }))).then(function(results) {
      if (alive && results.some(Boolean)) setCatalogVersion(function(v) { return v + 1; });
    });
    return function() { alive = false; };
  }, []);
  /* NOT memoized on purpose — this used to be a useMemo keyed off
     [existingVendors] (which only changes when the expense-bill list
     changes), so it froze whatever gfi-brokers/gfi-warehouses looked
     like the FIRST time this ran and never re-read them again, even
     after those catalogs got populated later. A vendor whose catalog
     record didn't exist yet at that first computation fell into the
     "other" bucket (via the existingVendors fallback below) and stayed
     there for the life of the mount — exactly the "Godinez shows as
     (OTHER)" bug. Recomputing every render is cheap (a handful of
     small arrays) and guarantees the category always reflects current
     localStorage. */
  var allVendorOptions = (function() {
    var map = {};
    ["gfi-suppliers", "gfi-clients", "gfi-warehouses", "gfi-brokers", "gfi-quality-control", "gfi-certifiers"].forEach(function(key) {
      var label = key.replace("gfi-", "").replace(/s$/, "");
      loadLS(key, []).forEach(function(item) {
        var name = item.shortName || item.fullName || item.name || item.companyName || "";
        if (name && !map[name]) map[name] = { name: name, category: label };
      });
    });
    existingVendors.forEach(function(v) { if (!map[v]) map[v] = { name: v, category: "other" }; });
    return Object.values(map).sort(function(a, b) { return a.name.localeCompare(b.name); });
  })();
  var customCats = useMemo(function() { return loadLS(CAT_STORAGE, []); }, []);
  var allCats = useMemo(function() { return [...DEFAULT_CATS, ...customCats.filter(function(c) { return !DEFAULT_CATS.includes(c); })]; }, [customCats]);
  /* catalogVersion isn't read inside the callback — it's an artificial
     trigger so this re-reads localStorage after the fee_catalog refresh
     above writes to it. eslint-disable-next-line since the "unnecessary
     dependency" warning doesn't know that. */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  var FEE_TYPES = useMemo(function() { try { var d = localStorage.getItem("gfi-fee-types"); return d ? JSON.parse(d) : []; } catch { return []; } }, [catalogVersion]);
  /* allocations: [{ billId, amount, allocate }] — a fee can now be
     split across several target bills. billId/amount(whole)/
     allocateToBill kept on the fee itself only for backward
     compatibility with records saved before this feature; a legacy
     fee with billId set but no allocations yet is migrated to a
     1-item allocations array on load, below. */
  var EMPTY_FEE = { feeType: "", feeCustom: "", qty: 1, rate: "", amount: "", shipmentId: "", billId: "", pctRate: "", allocateToBill: false, allocations: [] };

  var [f, setF] = useState(function() {
    if (bill) return { ...bill, payments: [...(bill.payments || [])], fees: (bill.fees || bill.lines || []).map(function(fe) {
      var merged = { ...EMPTY_FEE, ...fe };
      if ((!merged.allocations || merged.allocations.length === 0) && merged.billId) {
        merged = { ...merged, allocations: [{ billId: merged.billId, amount: merged.amount, allocate: !!merged.allocateToBill }] };
      }
      /* 2026-10-16, Arturo: "no me gusta que en un split, un campo
         muestra formato moneda con decimales y el otro no" — sibling
         allocation amounts could come from different capture moments
         (one typed by hand, one auto-split), landing as "40" vs
         "40.00" on the same fee. Normalize every allocation's amount
         to 2 decimals once, on load, so a split's rows always match —
         same as the onBlur normalization added to the input itself for
         any amount typed afterward. */
      if (merged.allocations && merged.allocations.length > 0) {
        merged = { ...merged, allocations: merged.allocations.map(function (al) {
          var n = parseFloat(al.amount);
          return isNaN(n) ? al : { ...al, amount: n.toFixed(2) };
        }) };
      }
      return merged;
    }), documents: bill.documents || [] };
    return { id: "", vendor: "", reference: "", date: new Date().toISOString().substring(0, 10), dueDate: "", category: "", entryNumber: "", notes: "", payments: [], fees: [{ ...EMPTY_FEE }], documents: [] };
  });
  var [fileBusy, setFileBusy] = useState(false);
  var [errors, setErrors] = useState({});
  var [showDel, setShowDel] = useState(false);
  var [dirty, setDirty] = useState(false);
  var [showExitConfirm, setShowExitConfirm] = useState(false);
  var [newCat, setNewCat] = useState(""); var [showNewCat, setShowNewCat] = useState(false);
  var [showPayForm, setShowPayForm] = useState(false);
  var [payDate, setPayDate] = useState(new Date().toISOString().substring(0, 10));
  var [payAmt, setPayAmt] = useState("");

  /* Date-only auto-recalc (same vendor, new date) — no confirm needed,
     it's just recomputing off whichever vendor is already selected.
     Vendor CHANGES go through handleVendorChange below instead, since
     switching vendors can clobber a due date the user already set by
     hand — this generic setter no longer special-cases "vendor" at all,
     which also removes the case/whitespace-sensitive duplicate of
     findVendorTermDays that used to live here (a likely cause of the
     "due date stays blank" symptom: it required an exact-case match
     while the Default button's findVendorTermDays already normalized
     case/whitespace). */
  var set = function(k, v) {
    setF(function(p) {
      var updated = { ...p, [k]: v };
      if (k === "date" && p.vendor) {
        var term = findVendorTermDays(p.vendor);
        if (term !== null) updated.dueDate = addDaysToISO(v, term);
      }
      return updated;
    }); setDirty(true);
  };
  /* Vendor change — auto-fills Due Date from the vendor's own Payment
     Term (broker/supplier/warehouse/client, matched by name via the
     same findVendorTermDays the Default button uses) when there's
     nothing to lose (Due Date still blank), otherwise confirms before
     overwriting a Due Date the user may have set by hand. */
  var handleVendorChange = function(v) {
    var days = findVendorTermDays(v);
    var newDueDate = (days !== null && f.date) ? addDaysToISO(f.date, days) : null;
    if (newDueDate && f.dueDate && f.dueDate !== newDueDate) {
      var msg = lang === "es"
        ? "El nuevo vendor tiene término de " + days + " días. ¿Actualizar la fecha de vencimiento?"
        : "The new vendor has a " + days + "-day term. Update the due date?";
      var updateDate = window.confirm(msg);
      setF(function(p) { return { ...p, vendor: v, dueDate: updateDate ? newDueDate : p.dueDate }; });
    } else {
      setF(function(p) { return { ...p, vendor: v, dueDate: newDueDate || p.dueDate }; });
    }
    setDirty(true);
  };

  var addFee = function() { setF(function(p) { return { ...p, fees: [...(p.fees||[]), {...EMPTY_FEE}] }; }); setDirty(true); };
  /* Drag-reorder for fees. */
  var dndFees = useDragReorder(f.fees, function(next) { setF(function(p) { return { ...p, fees: next }; }); setDirty(true); });
  var removeFee = function(idx) { setF(function(p) { return { ...p, fees: p.fees.filter(function(_,i){return i!==idx;}) }; }); setDirty(true); };
  var updateFee = function(idx, field, value) {
    setF(function(p) {
      var fees = p.fees.map(function(fe,i) {
        if (i!==idx) return fe;
        var u = { ...fe, [field]: value };
        if (field === "qty" || field === "rate") u.amount = ((parseInt(u.qty,10)||1)*(parseFloat(u.rate)||0)).toFixed(2);
        /* Lot is no longer a user-facing field — each shipment bill has a
           single lot, so shipmentId is auto-derived from the chosen bill
           (not typed by the user) and kept in sync so ProfitTracker's
           per-lot cost distribution (fe.shipmentId) keeps working. */
        if (field === "billId") {
          var mb = value ? availableBills.find(function(b){return b.id===value;}) : null;
          var shp = mb ? shipments.find(function(s){return s.lot===mb.lot;}) : null;
          u.shipmentId = shp ? shp.id : "";
        }
        return u;
      });
      return { ...p, fees: fees };
    }); setDirty(true);
  };

  /* 2026-10-16, Arturo: "el costo debo asignarlo a 2 embarques... ahora
     debo capturar 2 veces el mismo concepto para poder asignar el
     costo a cada lote... lo ideal seria capturar solo una vez y al
     asignar me permita decidir el costo a mas de un lote con su propio
     boton de allocate o solo reference" — one fee line (e.g. a single
     Godinez customs invoice) can now split its cost across SEVERAL
     target bills/lots, each with its own portion amount and its own
     independent Allocate/Reference-only toggle, instead of needing a
     whole duplicate fee row per lot. */
  /* 2026-10-29, Arturo: "al enviar un fee de una factura de un proveedor,
     automaticamente debe asignar la cantidad de la columna amount si se
     decide enviar el costo, y yo podre decidir manualmente si ese costo
     lo cambio o lo dejo igual" — each new split defaults its $ to the
     fee's own Amount column (the full cost) instead of starting blank;
     updateFeeAllocation (below) is still what lets Arturo overwrite it
     by hand afterward. */
  var addFeeAllocation = function(idx) {
    setF(function(p) {
      var fees = p.fees.map(function(fe, i) { return i !== idx ? fe : { ...fe, allocations: [...(fe.allocations || []), { billId: "", amount: fe.amount || "", allocate: true }] }; });
      return { ...p, fees: fees };
    }); setDirty(true);
  };
  var removeFeeAllocation = function(idx, aIdx) {
    setF(function(p) {
      var fees = p.fees.map(function(fe, i) { return i !== idx ? fe : { ...fe, allocations: (fe.allocations || []).filter(function(_, ai) { return ai !== aIdx; }) }; });
      return { ...p, fees: fees };
    }); setDirty(true);
  };
  var updateFeeAllocation = function(idx, aIdx, field, value) {
    setF(function(p) {
      var fees = p.fees.map(function(fe, i) {
        if (i !== idx) return fe;
        var allocations = (fe.allocations || []).map(function(al, ai) { return ai !== aIdx ? al : { ...al, [field]: value }; });
        return { ...fe, allocations: allocations };
      });
      return { ...p, fees: fees };
    }); setDirty(true);
  };

  var totalFees = 0; (f.fees||[]).forEach(function(fe){totalFees += parseFloat(fe.amount)||0;});
  var bal = calcBal({ ...f, amount: totalFees });
  var addPayment = function() { if (!payAmt||parseFloat(payAmt)<=0) return; setF(function(p){return{...p,payments:[...p.payments,{date:payDate,amount:parseFloat(payAmt).toFixed(2)}]};}); setShowPayForm(false); setPayAmt(""); setDirty(true); };
  var removePayment = function(idx) { setF(function(p){return{...p,payments:p.payments.filter(function(_,i){return i!==idx;})};}); setDirty(true); };

  /* Bill attachments (PDF/image, multiple) — each file uploads
     immediately to the shared shipment-docs bucket and appends
     {name,size,path,url,type} to f.documents. No cap on count.
     Shared by both the click-to-pick input and the FileDropZone
     drag-and-drop path; each dropped/selected file uploads and
     appends independently. */
  var uploadOneBillFile = async function(file) {
    var ext = (file.name.split(".").pop() || "bin").toLowerCase();
    var billIdForPath = f.id || ("tmp-" + Date.now());
    var path = "expense-bills/" + billIdForPath + "/bill-" + Date.now() + "-" + Math.random().toString(36).substring(2, 6) + "." + ext;
    var up = await uploadFileToStorage(EXPENSE_DOCS_BUCKET, path, file);
    var doc = { name: file.name, size: file.size, path: up.path, url: up.publicUrl, type: file.type };
    setF(function(p) { return { ...p, documents: [...(p.documents || []), doc] }; });
    setDirty(true);
  };
  var uploadBillFiles = async function(files) {
    var list = Array.from(files || []).filter(Boolean);
    if (list.length === 0) return;
    setFileBusy(true);
    try {
      for (var i = 0; i < list.length; i++) {
        await uploadOneBillFile(list[i]);
      }
    } catch (err) {
      alert("Upload error: " + (err.message || String(err)));
    } finally {
      setFileBusy(false);
    }
  };
  var handleFileSelect = function(e) {
    var files = e.target.files;
    e.target.value = "";
    uploadBillFiles(files);
  };
  var handleFilesDropped = function(files) { uploadBillFiles(files); };
  var handleDeleteFile = function(idx) {
    var doc = (f.documents || [])[idx];
    if (!doc) return;
    if (!window.confirm(t.confirmDelete)) return;
    if (doc.path) removeFromStorage(EXPENSE_DOCS_BUCKET, doc.path);
    setF(function(p) { return { ...p, documents: p.documents.filter(function(_, i) { return i !== idx; }) }; });
    setDirty(true);
  };

  var validate = function() { var e={}; if(!f.vendor) e.vendor=true; if(!(f.fees||[]).some(function(fe){return parseFloat(fe.amount)>0;})) e.fees=true; setErrors(e); return Object.keys(e).length===0; };
  var handleSave = async function() {
    if (!validate()) return;
    /* 2026-10-16, Arturo: a fee can now be split across several target
       bills/lots (fe.allocations), not just one — distribution (feeds
       ProfitTracker's per-lot cost) and fee-transfers (feeds the
       target Bill's accept/reject queue) are both built per-
       ALLOCATION now, using that allocation's own portion amount, not
       the fee's whole amount. fe.billId/shipmentId/allocateToBill are
       kept in sync to the FIRST allocation only, purely so older code
       paths that still read those singular fields (e.g. an
       already-printed PDF regenerated later) degrade gracefully
       instead of showing nothing. */
    var feesWithLot = (f.fees||[]).map(function(fe) {
      var first = (fe.allocations||[])[0];
      if (!first || !first.billId) return { ...fe, billId: "", shipmentId: "", allocateToBill: false };
      var mb = availableBills.find(function(b){return b.id===first.billId;});
      var shp = mb ? shipments.find(function(s){return s.lot===mb.lot;}) : null;
      return { ...fe, billId: first.billId, shipmentId: shp ? shp.id : "", allocateToBill: !!first.allocate };
    });
    var toSave = { ...f, fees: feesWithLot, id: f.id||nextId(bills), amount: totalFees.toFixed(2) };
    var distributionEntries = [];
    feesWithLot.forEach(function(fe) {
      (fe.allocations||[]).forEach(function(al) {
        if (!al.billId) return;
        var mb = availableBills.find(function(b){return b.id===al.billId;});
        var shp = mb ? shipments.find(function(s){return s.lot===mb.lot;}) : null;
        if (!shp) return;
        var p = 0; (shp.lines||[]).forEach(function(l){p+=parseInt(l.pallets,10)||0;});
        var cr = p>0?((parseFloat(al.amount)||0)/p).toFixed(2):"0";
        distributionEntries.push({ shipmentId: shp.id, lot: shp.lot, pallets: String(p), costRate: cr, amount: al.amount, category: f.category||"other" });
      });
    });
    toSave.distribution = distributionEntries;
    toSave.linkedLots = [...new Set(distributionEntries.map(function(d){return d.shipmentId;}))];
    try {
      /* 2026-10-16 — this whole pending-transfer queue only ever lived in
         THIS browser's localStorage, never Supabase, despite a
         "fee_transfers" table existing since a one-time 2026-05-19
         seed migration that nothing in the live app ever reads/writes
         (confirmed: 4 rows, all dated 2026-05-19, none created since).
         A transfer created on Arturo's own device was invisible to any
         other session opening the target Bill — exactly what he hit
         testing Expense Bill 2603177-5 → LOT 58060/58061 ("al abrir
         esos lotes, los fees no aparecen"). Now reads/writes Supabase
         (the real cross-device source of truth) with the same
         already-accepted/rejected-is-never-touched reconciliation as
         before (2026-09-14 fix, see comment history), localStorage kept
         only as the instant-read mirror other code in this file still
         uses for an unsaved-in-this-render fallback. */
      var transfers = await loadFromSupabase("fee_transfers", { force: true });
      var keyFor = function(billId, fname, amount) { return billId+"|"+fname+"|"+amount; };
      var currentKeys = {};
      feesWithLot.forEach(function(fe) {
        var fname = fe.feeType==="__custom__"?fe.feeCustom:(fe.feeType||"Expense");
        (fe.allocations||[]).forEach(function(al) {
          if (al.allocate && al.billId && parseFloat(al.amount)>0) currentKeys[keyFor(al.billId, fname, al.amount)] = true;
        });
      });
      var toDeleteIds = [];
      transfers = transfers.filter(function(tr){
        if (tr.sourceExpenseBill!==toSave.id) return true;
        if (tr.status !== "pending") return true;
        var fname = tr.feeType==="__custom__"?tr.feeCustom:tr.feeType;
        if (currentKeys[keyFor(tr.targetBillId, fname, tr.amount)]) return true;
        toDeleteIds.push(tr.id);
        return false;
      });
      var existingKeys = {};
      transfers.forEach(function(tr){
        if (tr.sourceExpenseBill!==toSave.id) return;
        var fname = tr.feeType==="__custom__"?tr.feeCustom:tr.feeType;
        existingKeys[keyFor(tr.targetBillId, fname, tr.amount)] = true;
      });
      var toCreate = [];
      feesWithLot.forEach(function(fe) {
        var fname = fe.feeType==="__custom__"?fe.feeCustom:(fe.feeType||"Expense");
        (fe.allocations||[]).forEach(function(al) {
          if (!(al.allocate && al.billId && parseFloat(al.amount)>0)) return;
          var k = keyFor(al.billId, fname, al.amount);
          if (existingKeys[k]) return;
          var tr = {id:"TRF-"+Date.now()+"-"+Math.random().toString(36).substring(2,6),sourceType:"expense",sourceExpenseBill:toSave.id,targetType:"bill",targetBillId:al.billId,feeType:fname,feeCustom:fe.feeCustom||"",amount:al.amount,status:"pending",dateCreated:new Date().toISOString().substring(0,10)};
          existingKeys[k] = true;
          toCreate.push(tr);
          transfers.push(tr);
        });
      });
      if (toCreate.length > 0) await saveManyToSupabase("fee_transfers", toCreate);
      await Promise.all(toDeleteIds.map(function(id) { return deleteFromSupabase("fee_transfers", id).catch(function(){}); }));
      localStorage.setItem("gfi-fee-transfers",JSON.stringify(transfers));
    } catch(e){ console.error("Fee transfer sync error", e); }
    onSave(toSave);
  };
  var tryCancel = function() { if(dirty) setShowExitConfirm(true); else onCancel(); };

  var body = (
    <>
        <div style={{flex:1,overflowY:"auto"}}>
          <div style={{padding:"12px 20px 0",display:"grid",gridTemplateColumns:"1.5fr 1fr 1fr",gap:8}}>
            <div><div style={{fontSize:10,color:"var(--color-text-muted)",marginBottom:2}}>{t.vendor}*</div><SearchSelect value={f.vendor||""} onChange={handleVendorChange} placeholder={t.selectVendor} error={errors.vendor} options={allVendorOptions.map(function(v){return{value:v.name,label:v.name+" ("+v.category+")",category:v.category};})} /></div>
            <div><div style={{fontSize:10,color:"var(--color-text-muted)",marginBottom:2}}>{t.reference}</div><input value={f.reference||""} onChange={function(e){set("reference",e.target.value);}} style={{...inp,fontFamily:"'JetBrains Mono', monospace"}} /></div>
            <div><div style={{fontSize:10,color:"var(--color-text-muted)",marginBottom:2}}>{t.selectCategory}</div><SearchSelect value={f.category||""} onChange={function(v){if(v==="__new__")setShowNewCat(true);else set("category",v);}} placeholder={t.selectCategory} options={[...allCats.map(function(c){return{value:c,label:t[catKeys[c]]||c};}),{value:"__new__",label:"+ "+t.addCategory}]} />{showNewCat&&<div style={{display:"flex",gap:4,marginTop:3}}><input value={newCat} onChange={function(e){setNewCat(e.target.value);}} style={{...inp,borderStyle:"dashed",flex:1,fontSize:10}} /><button onClick={function(){if(newCat.trim()){try{localStorage.setItem(CAT_STORAGE,JSON.stringify([...customCats,newCat.trim()]));}catch{}set("category",newCat.trim());setNewCat("");setShowNewCat(false);}}} style={{padding:"4px 8px",borderRadius:4,border:"none",background:"var(--color-accent)",color:"var(--color-accent-text)",cursor:"pointer",fontSize:10}}>+</button></div>}</div>
          </div>
          <div style={{padding:"6px 20px 10px",display:"grid",gridTemplateColumns:f.category==="customs"?"1fr 1fr 1fr":"1fr 1fr",gap:8}}>
            <div><div style={{fontSize:10,color:"var(--color-text-muted)",marginBottom:2}}>{t.date}</div><input type="date" value={f.date} onChange={function(e){set("date",e.target.value);}} style={inp} /></div>
            <div>
              <div style={{fontSize:10,color:"var(--color-text-muted)",marginBottom:2,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <span>{t.dueDate}</span>
                <button type="button" onClick={function(){
                  var days = findVendorTermDays(f.vendor);
                  if (days === null) days = 30;
                  set("dueDate", addDaysToISO(f.date, days));
                }} style={{fontSize:9,padding:"1px 6px",border:"1px solid var(--color-border)",borderRadius:4,background:"transparent",color:"var(--color-accent)",cursor:"pointer",fontWeight:600}} title="Calculate from Date + Vendor Term">↻ Default</button>
              </div>
              <input type="date" value={f.dueDate||""} onChange={function(e){set("dueDate",e.target.value);}} style={inp} />
            </div>
            {f.category==="customs"&&<div><div style={{fontSize:10,color:"var(--color-text-muted)",marginBottom:2}}>{t.entryNumber}</div><input value={f.entryNumber||""} onChange={function(e){set("entryNumber",e.target.value);}} style={{...inp,fontFamily:"'JetBrains Mono', monospace"}} /></div>}
          </div>

          <div style={{padding:"0 20px",borderTop:"1px solid var(--color-border)"}}>
            <div style={{fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.08em",color:"var(--color-accent)",margin:"10px 0 8px",paddingBottom:4,borderBottom:"1px solid var(--color-border)"}}>{t.lines}</div>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:12,tableLayout:"fixed"}}>
              <thead><tr style={{borderBottom:"1px solid var(--color-border)"}}>
                <th style={{width:"3%"}}></th>
                <th style={{padding:"6px 4px",textAlign:"left",fontWeight:700,fontSize:10,color:"var(--color-text-secondary)",width:"22%"}}>Fee Type</th>
                <th style={{padding:"6px 3px",textAlign:"center",fontWeight:700,fontSize:10,color:"var(--color-text-secondary)",width:"7%"}}>Qty</th>
                <th style={{padding:"6px 3px",textAlign:"right",fontWeight:700,fontSize:10,color:"var(--color-text-secondary)",width:"10%"}}>Rate</th>
                <th style={{padding:"6px 3px",textAlign:"right",fontWeight:700,fontSize:10,color:"var(--color-text-secondary)",width:"14%"}}>{t.lineAmount}</th>
                <th style={{padding:"6px 3px",textAlign:"left",fontWeight:700,fontSize:10,color:"var(--color-text-secondary)",width:"30%"}}>{t.lineBill}</th>
                <th style={{width:"4%"}}></th>
              </tr></thead>
              <tbody>{(f.fees||[]).map(function(fee,idx) {
                var isFinancing = fee.feeType==="Advance Financing Fee";
                var rp = dndFees.rowProps(idx);
                return <tr key={idx} {...rp} style={{borderBottom:"1px solid var(--color-border)", ...(rp.style || {})}}>
                  <td style={{padding:"5px 0",textAlign:"center"}}><DragHandle handleProps={dndFees.handleProps()} /></td>
                  <td style={{padding:"5px 3px"}}><SearchSelect value={fee.feeType||""} onChange={function(v){updateFee(idx,"feeType",v);}} placeholder="Fee type" options={[...FEE_TYPES.map(function(ft){return{value:ft,label:ft};}),{value:"__custom__",label:lang==="es"?"Otro (personalizado)":"Other (custom)"}]} />{fee.feeType==="__custom__"&&<input value={fee.feeCustom||""} onChange={function(e){updateFee(idx,"feeCustom",e.target.value);}} placeholder={lang==="es"?"Nombre":"Name"} style={{...inp,marginTop:3,fontSize:10,borderStyle:"dashed"}} />}{isFinancing&&<div style={{display:"flex",alignItems:"center",gap:4,marginTop:3}}><input type="number" min="0" max="100" step="0.1" value={fee.pctRate||""} onChange={function(e){updateFee(idx,"pctRate",e.target.value);}} style={{...inp,width:40,fontSize:9,textAlign:"center",fontFamily:"'JetBrains Mono', monospace"}} placeholder="%" /><span style={{fontSize:9,color:"var(--color-text-muted)"}}>% ref</span></div>}</td>
                  <td style={{padding:"5px 3px"}}><input type="number" min="1" value={fee.qty} onChange={function(e){updateFee(idx,"qty",e.target.value);}} style={{...inp,textAlign:"center",fontFamily:"'JetBrains Mono', monospace",fontSize:11}} /></td>
                  <td style={{padding:"5px 3px"}}><input type="number" min="0" step="0.01" value={fee.rate} onChange={function(e){updateFee(idx,"rate",e.target.value);}} style={{...inp,textAlign:"right",fontFamily:"'JetBrains Mono', monospace",fontSize:11}} /></td>
                  <td style={{padding:"5px 3px"}}><input type="number" min="0" step="0.01" value={fee.amount} onChange={function(e){updateFee(idx,"amount",e.target.value);}} style={{...inp,textAlign:"right",fontFamily:"'JetBrains Mono', monospace",fontSize:11,fontWeight:600}} /></td>
                  <td style={{padding:"5px 3px"}}>
                    {/* 2026-10-16, Arturo: one fee can now be split
                        across several target bills/lots, each with its
                        own portion amount and its own Allocate/
                        Reference-only toggle \u2014 "al estar en modo editar
                        debe mostrar a que lote se asigno cada concepto,
                        solo muestra una palomita": every allocation row
                        keeps its own Bill picker (shows "LOT # shipper"
                        right there, not just a checkmark), its own $
                        input, and its own \u2715 to remove just that split. */}
                    {(fee.allocations || []).map(function(al, aIdx) {
                      return (
                        <div key={aIdx} style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 4 }}>
                          <div style={{ flex: 1 }}>
                            <SearchSelect value={al.billId || ""} onChange={function(v){updateFeeAllocation(idx,aIdx,"billId",v);}} placeholder={"\u2192 Bill"} options={sortedAvailableBills.map(function(b){return{value:b.id,label:"LOT "+b.lot+" "+(b.shipperName||"")};})} />
                          </div>
                          <input type="number" min="0" step="0.01" value={al.amount} onChange={function(e){updateFeeAllocation(idx,aIdx,"amount",e.target.value);}} onBlur={function(e){var n=parseFloat(e.target.value); if(!isNaN(n)) updateFeeAllocation(idx,aIdx,"amount",n.toFixed(2));}} placeholder="$" style={{...inp,width:60,textAlign:"right",fontFamily:"'JetBrains Mono', monospace",fontSize:10}} />
                          <label title={al.allocate ? t.allocatedBadge : t.referenceOnly} style={{display:"flex",alignItems:"center",cursor:al.billId?"pointer":"not-allowed",opacity:al.billId?1:0.5}}>
                            <input type="checkbox" checked={!!al.allocate} disabled={!al.billId} onChange={function(e){updateFeeAllocation(idx,aIdx,"allocate",e.target.checked);}} style={{margin:0}} />
                          </label>
                          <button type="button" onClick={function(){removeFeeAllocation(idx,aIdx);}} style={{background:"none",border:"none",color:"var(--color-text-muted)",cursor:"pointer",fontSize:10,padding:0}}>{"\u2715"}</button>
                        </div>
                      );
                    })}
                    <button type="button" onClick={function(){addFeeAllocation(idx);}} style={{padding:"2px 8px",borderRadius:5,border:"1px dashed var(--color-border)",background:"transparent",color:"var(--color-accent)",cursor:"pointer",fontSize:9,fontWeight:600}}>{"+ " + t.sendToBill}</button>
                  </td>
                  <td style={{padding:"5px 1px",textAlign:"center"}}><button onClick={function(){removeFee(idx);}} style={{background:"none",border:"none",color:"var(--color-text-muted)",cursor:"pointer",fontSize:12}}>{"\u2715"}</button></td>
                </tr>;
              })}</tbody>
              <tfoot><tr><td colSpan={4} style={{padding:"8px 4px"}}><button onClick={addFee} style={{padding:"4px 12px",borderRadius:6,border:"1px dashed var(--color-border)",background:"transparent",color:"var(--color-accent)",cursor:"pointer",fontSize:11,fontWeight:600}}>+ {t.addLine}</button></td><td style={{padding:"8px 3px",textAlign:"right",fontFamily:"'JetBrains Mono', monospace",fontSize:12,fontWeight:700}}>{fmtMoney(totalFees)}</td><td colSpan={3}></td></tr></tfoot>
            </table>
          </div>

          <div style={{ padding: "10px 20px 6px" }}>
            <PaymentsSection payments={f.payments} total={bal.total} t={t} />
          </div>
          <div style={{padding:"6px 20px 12px"}}>
            <div style={{fontSize:10,fontWeight:700,color:"var(--color-text-muted)",marginBottom:4,textTransform:"uppercase"}}>{t.attachBill}</div>
            <FileDropZone onFiles={handleFilesDropped} multiple={true} disabled={fileBusy} lang={lang} style={{width:"100%"}}>
              <label style={{display:"flex",alignItems:"center",justifyContent:"center",gap:5,padding:"8px 14px",borderRadius:6,border:"1px dashed var(--color-border)",background:"transparent",color:"var(--color-accent)",cursor:fileBusy?"wait":"pointer",fontSize:11,fontWeight:600,width:"100%",boxSizing:"border-box"}}>
                <span style={{fontSize:14,lineHeight:1}}>+</span> {fileBusy ? "…" : t.attachBill}
                <input type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.gif,.webp" onChange={handleFileSelect} disabled={fileBusy} style={{display:"none"}} />
              </label>
            </FileDropZone>
            {(f.documents || []).length > 0 && (
              <div style={{display:"flex",flexDirection:"column",gap:4,marginTop:6}}>
                {f.documents.map(function(doc, idx) {
                  return (
                    <div key={idx} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 10px",background:"var(--color-bg)",borderRadius:6,border:"1px solid var(--color-border)"}}>
                      <span style={{fontSize:13}}>📎</span>
                      <a href={doc.url} target="_blank" rel="noopener noreferrer" title={t.viewFile} style={{flex:1,fontSize:11,fontWeight:500,color:"var(--color-accent)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{doc.name}</a>
                      <span style={{fontSize:10,color:"var(--color-text-muted)",whiteSpace:"nowrap"}}>{fmtFileSize(doc.size)}</span>
                      <button type="button" onClick={function() { handleDeleteFile(idx); }} disabled={fileBusy} style={{fontSize:11,color:"#ef4444",fontWeight:600,cursor:fileBusy?"wait":"pointer",background:"none",border:"none",padding:0}}>{t.deleteFile}</button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div style={{padding:"6px 20px 12px"}}><div style={{fontSize:10,fontWeight:700,color:"var(--color-text-muted)",marginBottom:2,textTransform:"uppercase"}}>{t.notes}</div><textarea value={f.notes||""} onChange={function(e){set("notes",e.target.value);}} rows={2} style={{...inp,resize:"vertical",fontSize:11}} /></div>
        </div>
        <div style={{padding:"10px 20px",borderTop:"1px solid var(--color-border)",display:"flex",justifyContent:"space-between"}}>
          <div>{!isNew&&!showDel&&<button onClick={function(){setShowDel(true);}} style={{padding:"5px 12px",borderRadius:6,border:"1px solid rgba(239,68,68,0.3)",background:"transparent",color:"#ef4444",cursor:"pointer",fontSize:11,fontWeight:600}}>{t.delete}</button>}{showDel&&<div style={{display:"flex",alignItems:"center",gap:6}}><span style={{fontSize:11,color:"#ef4444"}}>{t.confirmDel}</span><button onClick={function(){onDelete(bill.id);}} style={{padding:"4px 10px",borderRadius:4,border:"none",background:"#ef4444",color:"#fff",cursor:"pointer",fontSize:11}}>{t.yes}</button><button onClick={function(){setShowDel(false);}} style={{padding:"4px 10px",borderRadius:4,border:"1px solid var(--color-border)",background:"transparent",cursor:"pointer",fontSize:11}}>{t.no}</button></div>}</div>
          <div style={{display:"flex",gap:8}}>
            {!isNew && <button onClick={function() { var w = window.open("", "_blank"); if (!w) return; var html = buildExpenseBillPrintHTML(f); w.document.open(); w.document.write(html); w.document.close(); setTimeout(function() { try { w.focus(); w.print(); } catch(e) {} }, 600); }} style={{padding:"7px 14px",borderRadius:6,border:"1px solid #1B3A4B",background:"transparent",color:"#1B3A4B",cursor:"pointer",fontSize:11,fontWeight:600}}>{"🖨"} {t.printPreview}</button>}
            <button onClick={tryCancel} style={{padding:"7px 16px",borderRadius:6,border:"1px solid var(--color-border)",background:"transparent",color:"var(--color-text-secondary)",cursor:"pointer",fontSize:12}}>{t.cancel}</button>
            <button onClick={handleSave} style={{padding:"7px 20px",borderRadius:6,border:"none",background:"var(--color-accent)",color:"var(--color-accent-text)",cursor:"pointer",fontSize:12,fontWeight:600}}>{t.save}</button>
          </div>
        </div>
        {showExitConfirm&&<div style={{position:"absolute",inset:0,background:"rgba(0,0,0,0.4)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:10}}><div style={{background:"var(--color-bg-card-solid)",padding:24,borderRadius:12,maxWidth:300,textAlign:"center"}}><p style={{fontSize:13,marginBottom:14}}>{t.confirmExit}</p><div style={{display:"flex",gap:8,justifyContent:"center"}}><button onClick={onCancel} style={{padding:"6px 14px",borderRadius:6,border:"none",background:"#ef4444",color:"#fff",cursor:"pointer",fontSize:12,fontWeight:600}}>{t.yesExit}</button><button onClick={function(){setShowExitConfirm(false);}} style={{padding:"6px 14px",borderRadius:6,border:"1px solid var(--color-border)",background:"transparent",cursor:"pointer",fontSize:12}}>{t.noExit}</button></div></div></div>}
    </>
  );
  /* 2026-10-16 (Arturo: "al guardar un expense bill, me saca
     completamente del bill y me regresa a la tabla, necesito al
     guardarlo aun quedar dentro del bill en modo de vista y dentro de
     el agrega un boton de editar") — same embedded pattern already
     used by BillForm/ShipmentRegistry's edit form: when opened inside
     ExpenseBillDetailPanel's own window (embedded=true), return just
     the body so there's one single DraggableModal, not two stacked
     ones; the standalone "+ Add" / global-search-open flow for a
     brand-new record still gets its own modal here. */
  if (embedded) return body;
  return (
    <DraggableModal title={(isNew ? t.add : t.edit) + (f.reference ? "  ·  " + f.reference : "")} onClose={tryCancel} initialWidth={800} initialHeight={Math.min(900, window.innerHeight - 60)}>
      {body}
    </DraggableModal>
  );
}

/* ======================== DETAIL PANEL (view + edit, REGLA DE VENTANAS) ========================
   2026-10-16, Arturo: "al guardar un expense bill, me saca
   completamente del bill y me regresa a la tabla, necesito al
   guardarlo aun quedar dentro del bill en modo de vista y dentro de
   el agrega un boton de editar (si no lo tiene)" — this module never
   had a view mode at all (clicking a row opened the editable form
   directly, and saving/canceling always closed it outright). Same
   single-window view/edit toggle pattern as BillRegistry.js's
   DetailPanel: one DraggableModal, an internal `mode` state, Edit in
   the title bar switches to the embedded ExpenseForm without opening
   a second window, and saving switches back to view instead of
   closing. Opening a brand-new record still uses the standalone
   ExpenseForm modal (showForm/editing in the parent component) — this
   panel is only for an EXISTING record. */
function ExpenseBillDetailPanel({ bill, bills, t, lang, onSave, onDelete, onClose }) {
  var [mode, setMode] = useState("view");
  var fresh = bill;
  /* 2026-10-16 — the `bills` prop here is actually this component's
     OWN table (expense_bills, passed down from the parent registry's
     `bills` state) — not the real shipment Bills a fee allocation
     points to. Looking up al.billId against it always missed, so the
     view-mode table printed "LOT —" for every allocation even though
     the amount/checkmark were correct. Load the real `bills` table
     the same way ExpenseForm's own `availableBills` does, with the
     shared gfi-bills mirror as an instant fallback. */
  var [realBills, setRealBills] = useState(function () { return getBills(); });
  useEffect(function () {
    var alive = true;
    loadFromSupabase("bills", { force: true })
      .then(function (rows) { if (alive) setRealBills(rows || []); })
      .catch(function () { /* keep the localStorage fallback already in state */ });
    return function () { alive = false; };
  }, []);
  var handleSaveInline = function (updated) { onSave(updated); setMode("view"); };
  var handleDeleteInline = function (id) { onDelete(id); onClose(); };
  var st = getStatus(fresh);
  var ss = STATUS_STYLES[st] || STATUS_STYLES.draft;
  var bal = calcBal(fresh);
  var fees = fresh.fees || fresh.lines || [];

  return (
    <DraggableModal
      title={(fresh.vendor || "—") + (fresh.reference ? "  ·  " + fresh.reference : "") + (mode === "edit" ? "  ·  " + t.edit : "")}
      titleBarActions={mode === "view" ? (
        <>
          <span style={{ padding: "3px 10px", borderRadius: 0, fontSize: 10, fontWeight: 700, background: ss.bg, color: ss.text, border: "1px solid " + ss.border, boxShadow: STATUS_BEVEL, whiteSpace: "nowrap" }}>{t[stKeys[st]]}</span>
          <button type="button" onClick={function () { setMode("edit"); }} style={{ padding: "5px 12px", borderRadius: 6, border: "1px solid var(--color-border)", background: "#fff", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 11, fontWeight: 600, whiteSpace: "nowrap" }}>{t.editBtn}</button>
        </>
      ) : null}
      onClose={onClose} initialWidth={800} initialHeight={Math.min(900, window.innerHeight - 60)}
    >
      {mode === "edit" ? (
        <ExpenseForm bill={fresh} bills={bills} t={t} lang={lang} onSave={handleSaveInline} onDelete={handleDeleteInline} onCancel={function () { setMode("view"); }} embedded={true} />
      ) : (
        <>
          <div style={{ flex: 1, overflowY: "auto" }}>
            <div style={{ padding: "16px 20px 0", display: "grid", gridTemplateColumns: "1.5fr 1fr 1fr", gap: 8, fontSize: 12 }}>
              <div><div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 2 }}>{t.vendor}</div><div style={{ fontWeight: 600 }}>{fresh.vendor || "—"}</div></div>
              <div><div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 2 }}>{t.reference}</div><div style={{ fontFamily: "'JetBrains Mono', monospace" }}>{fresh.reference || "—"}</div></div>
              <div><div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 2 }}>{t.selectCategory}</div><div>{fresh.category ? (t[catKeys[fresh.category]] || fresh.category) : "—"}</div></div>
            </div>
            <div style={{ padding: "8px 20px 0", display: "grid", gridTemplateColumns: fresh.category === "customs" ? "1fr 1fr 1fr" : "1fr 1fr", gap: 8, fontSize: 12 }}>
              <div><div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 2 }}>{t.date}</div><div>{fresh.date || "—"}</div></div>
              <div><div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 2 }}>{t.dueDate}</div><div>{fresh.dueDate || "—"}</div></div>
              {fresh.category === "customs" && <div><div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 2 }}>{t.entryNumber}</div><div style={{ fontFamily: "'JetBrains Mono', monospace" }}>{fresh.entryNumber || "—"}</div></div>}
            </div>

            <div style={{ padding: "0 20px" }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--color-accent)", margin: "14px 0 8px", paddingBottom: 4, borderBottom: "1px solid var(--color-border)" }}>{t.lines}</div>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead><tr style={{ borderBottom: "1px solid var(--color-border)" }}>
                  <th style={{ padding: "6px 4px", textAlign: "left", fontWeight: 700, fontSize: 10, color: "var(--color-text-secondary)" }}>Fee Type</th>
                  <th style={{ padding: "6px 3px", textAlign: "center", fontWeight: 700, fontSize: 10, color: "var(--color-text-secondary)" }}>Qty</th>
                  <th style={{ padding: "6px 3px", textAlign: "right", fontWeight: 700, fontSize: 10, color: "var(--color-text-secondary)" }}>Rate</th>
                  <th style={{ padding: "6px 3px", textAlign: "right", fontWeight: 700, fontSize: 10, color: "var(--color-text-secondary)" }}>{t.lineAmount}</th>
                  <th style={{ padding: "6px 3px", textAlign: "left", fontWeight: 700, fontSize: 10, color: "var(--color-text-secondary)" }}>{t.lineBill}</th>
                </tr></thead>
                <tbody>{fees.map(function (fee, idx) {
                  var feeName = fee.feeType === "__custom__" ? (fee.feeCustom || "—") : (fee.feeType || "—");
                  /* 2026-10-16 — a fee can now be split across several
                     target bills; show every allocation, each with its
                     own LOT # and its own amount (not just one bill +
                     a checkmark). Arturo: "en el modo actual no muestra
                     a lotes se asigno, necesito eso sea visible no solo
                     en modo edicion" — this view renders the bill
                     exactly as SAVED in Supabase; the allocations[]
                     migration only ever ran inside ExpenseForm's own
                     load (edit mode), so every fee saved before this
                     feature still has nothing there and rendered blank
                     here. Same fallback now applies in view mode too:
                     a fee with no allocations[] yet but a legacy
                     billId is shown as its own 1-item list. */
                  var allocs = (fee.allocations && fee.allocations.length > 0)
                    ? fee.allocations.filter(function (al) { return al.billId; })
                    : (fee.billId ? [{ billId: fee.billId, amount: fee.amount, allocate: !!fee.allocateToBill }] : []);
                  return (
                    <tr key={idx} style={{ borderBottom: "1px solid var(--color-border)" }}>
                      <td style={{ padding: "6px 4px" }}>{feeName}</td>
                      <td style={{ padding: "6px 3px", textAlign: "center", fontFamily: "'JetBrains Mono', monospace" }}>{fee.qty}</td>
                      <td style={{ padding: "6px 3px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace" }}>{fmtMoney(fee.rate)}</td>
                      <td style={{ padding: "6px 3px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }}>{fmtMoney(fee.amount)}</td>
                      <td style={{ padding: "6px 3px", fontSize: 11 }}>
                        {allocs.length === 0 ? "—" : allocs.map(function (al, ai) {
                          var b2 = realBills.find(function (bb) { return bb.id === al.billId; });
                          return <div key={ai} style={{ whiteSpace: "nowrap" }}>{"LOT " + (b2 ? b2.lot : "—") + " · " + fmtMoney(al.amount) + (al.allocate ? " ✓" : " (" + t.referenceOnly + ")")}</div>;
                        })}
                      </td>
                    </tr>
                  );
                })}</tbody>
                <tfoot><tr><td colSpan={3} style={{ padding: "8px 4px", textAlign: "right", fontWeight: 700, fontSize: 11, color: "var(--color-text-secondary)" }}>{t.lineTotal}</td><td style={{ padding: "8px 3px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 700 }}>{fmtMoney(bal.total)}</td><td></td></tr></tfoot>
              </table>
            </div>

            <div style={{ padding: "10px 20px 6px" }}>
              <PaymentsSection payments={fresh.payments} total={bal.total} t={t} />
            </div>

            {(fresh.documents || []).length > 0 && (
              <div style={{ padding: "6px 20px 12px" }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "var(--color-text-muted)", marginBottom: 4, textTransform: "uppercase" }}>{t.attachBill}</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {fresh.documents.map(function (doc, idx) {
                    return (
                      <div key={idx} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", background: "var(--color-bg)", borderRadius: 6, border: "1px solid var(--color-border)" }}>
                        <span style={{ fontSize: 13 }}>{"📎"}</span>
                        <a href={doc.url} target="_blank" rel="noopener noreferrer" title={t.viewFile} style={{ flex: 1, fontSize: 11, fontWeight: 500, color: "var(--color-accent)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{doc.name}</a>
                        <span style={{ fontSize: 10, color: "var(--color-text-muted)", whiteSpace: "nowrap" }}>{fmtFileSize(doc.size)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {fresh.notes && (
              <div style={{ padding: "6px 20px 16px" }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "var(--color-text-muted)", marginBottom: 2, textTransform: "uppercase" }}>{t.notes}</div>
                <div style={{ fontSize: 12, whiteSpace: "pre-wrap" }}>{fresh.notes}</div>
              </div>
            )}
          </div>
          <div style={{ padding: "10px 20px", borderTop: "1px solid var(--color-border)", display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button onClick={function () { var w = window.open("", "_blank"); if (!w) return; var html = buildExpenseBillPrintHTML(fresh); w.document.open(); w.document.write(html); w.document.close(); setTimeout(function () { try { w.focus(); w.print(); } catch (e) {} }, 600); }} style={{ padding: "7px 14px", borderRadius: 6, border: "1px solid #1B3A4B", background: "transparent", color: "#1B3A4B", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{"🖨"} {t.printPreview}</button>
          </div>
        </>
      )}
    </DraggableModal>
  );
}

/* ======================== BATCH PAYMENT MODAL ======================== */
function buildExpenseRef(bill) {
  var parts = [];
  if (bill.id) parts.push(bill.id);
  if (bill.reference) parts.push(bill.reference);
  if (bill.entryNumber) parts.push(bill.entryNumber);
  return parts.join(" · ");
}

function BatchPaymentModal({ bills, t, lang, onConfirm, onCancel }) {
  var initRows = useMemo(function () {
    return bills.map(function (b) {
      var bal = calcBal(b);
      return { bill: b, total: bal.total, paid: bal.paid, due: +bal.balance.toFixed(2), amount: bal.balance > 0 ? +bal.balance.toFixed(2) : 0 };
    });
  }, [bills]);

  var [rows, setRows] = useState(initRows);
  var [paymentDate, setPaymentDate] = useState(todayISO());
  var [paymentMethod, setPaymentMethod] = useState("wire");
  var [reference, setReference] = useState("");
  var [notes, setNotes] = useState("");
  var [error, setError] = useState("");
  /* 2026-09-15 — Arturo: "para esos pagos agrega al momento de hacerlo
     debe tener fecha de procesamiento, fecha de delivery" — two new
     required dates captured when recording an expense-bill payment,
     separate from paymentDate (when GFI issued the payment): when the
     bank/processor actually processed it, and when it's expected to
     land with the vendor. */
  var [processingDate, setProcessingDate] = useState(todayISO());
  var [deliveryDate, setDeliveryDate] = useState("");

  var openRows = useMemo(function () { return rows.filter(function (r) { return r.due > 0.01; }); }, [rows]);
  var total = useMemo(function () { return openRows.reduce(function (s, r) { return s + (parseFloat(r.amount) || 0); }, 0); }, [openRows]);

  var setRowAmount = function (billId, raw) {
    var v = raw === "" ? "" : parseFloat(raw);
    setRows(function (prev) { return prev.map(function (r) { return r.bill.id === billId ? { ...r, amount: v === "" ? "" : v } : r; }); });
    setError("");
  };

  var validate = function () {
    if (!paymentDate) return lang === "es" ? "Selecciona una fecha" : "Select a date";
    if (!processingDate) return lang === "es" ? "Selecciona la fecha de procesamiento" : "Select the processing date";
    if (!deliveryDate) return lang === "es" ? "Selecciona la fecha de entrega" : "Select the delivery date";
    for (var i = 0; i < openRows.length; i++) {
      var r = openRows[i];
      var amt = parseFloat(r.amount) || 0;
      if (amt < 0) return t.overApply + " (" + r.bill.id + ")";
      if (amt > r.due + 0.01) return t.overApply + " (" + r.bill.id + ")";
    }
    if (total <= 0) return lang === "es" ? "Captura al menos un monto > 0" : "Enter at least one amount > 0";
    return null;
  };

  var handleConfirm = function () {
    var err = validate();
    if (err) { setError(err); return; }
    var allocations = openRows
      .filter(function (r) { return (parseFloat(r.amount) || 0) > 0; })
      .map(function (r) { return { expenseBillId: r.bill.id, vendor: r.bill.vendor || "", reference: r.bill.reference || "", amount: +(parseFloat(r.amount) || 0).toFixed(2) }; });
    onConfirm({ paymentDate: paymentDate, processingDate: processingDate, deliveryDate: deliveryDate, paymentMethod: paymentMethod, reference: reference, notes: notes, allocations: allocations });
  };

  var methodLabel = function (m) { return t["method" + m.charAt(0).toUpperCase() + m.slice(1)] || m; };

  return (
    <DraggableModal title={t.batchPaymentTitle} onClose={onCancel} initialWidth={920} initialHeight={680}>
        <div style={{ flex: 1, overflowY: "auto", padding: "18px 24px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 2fr", gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", display: "block", marginBottom: 4 }}>{t.paymentDate} *</label>
              <input type="date" value={paymentDate} onChange={function (e) { setPaymentDate(e.target.value); }} style={inp} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", display: "block", marginBottom: 4 }}>{t.paymentMethod}</label>
              <SearchSelect value={paymentMethod} onChange={setPaymentMethod} options={PAYMENT_METHODS.map(function (m) { return { value: m, label: methodLabel(m) }; })} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", display: "block", marginBottom: 4 }}>{t.paymentReference}</label>
              <input type="text" value={reference} onChange={function (e) { setReference(e.target.value); }} style={{ ...inp, fontFamily: "'JetBrains Mono', monospace" }} placeholder="e.g. WIRE-2026-0123" />
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 2fr", gap: 12, marginBottom: 16 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", display: "block", marginBottom: 4 }}>{t.processingDate} *</label>
              <input type="date" value={processingDate} onChange={function (e) { setProcessingDate(e.target.value); }} style={inp} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", display: "block", marginBottom: 4 }}>{t.deliveryDate} *</label>
              <input type="date" value={deliveryDate} onChange={function (e) { setDeliveryDate(e.target.value); }} style={inp} />
            </div>
          </div>

          {openRows.length === 0 ? (
            <div style={{ padding: "32px 16px", textAlign: "center", color: "var(--color-text-muted)", fontSize: 13, background: "var(--color-bg)", borderRadius: 10, border: "1px dashed var(--color-border)" }}>{t.noOpenBills}</div>
          ) : (
            <div style={{ borderRadius: 10, border: "1px solid var(--color-border)", overflow: "hidden" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead><tr style={{ background: "var(--color-bg)", borderBottom: "2px solid var(--color-border)" }}>
                  <th style={{ padding: "8px 10px", textAlign: "left", fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "var(--color-text-secondary)" }}>{t.vendor}</th>
                  <th style={{ padding: "8px 10px", textAlign: "left", fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "var(--color-text-secondary)" }}>{t.refColumn}</th>
                  <th style={{ padding: "8px 10px", textAlign: "center", fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "var(--color-text-secondary)" }}>{t.dueDate}</th>
                  <th style={{ padding: "8px 10px", textAlign: "right", fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "var(--color-text-secondary)" }}>{t.dueAmount}</th>
                  <th style={{ padding: "8px 10px", textAlign: "right", fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "var(--color-text-secondary)" }}>{t.makePayment}</th>
                </tr></thead>
                <tbody>{openRows.map(function (r) {
                  var b = r.bill;
                  return <tr key={b.id} style={{ borderBottom: "1px solid var(--color-border)" }}>
                    <td style={{ padding: "10px", fontSize: 12, fontWeight: 600 }}>{b.vendor || "—"}</td>
                    <td style={{ padding: "10px", fontSize: 11, fontFamily: "'JetBrains Mono', monospace", color: "var(--color-text-secondary)" }}>{buildExpenseRef(b)}</td>
                    <td style={{ padding: "10px", textAlign: "center", fontSize: 11, color: "var(--color-text-secondary)" }}>{fmtDate(b.dueDate)}</td>
                    <td style={{ padding: "10px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 700, color: "#DC2626" }}>{fmtMoney(r.due)}</td>
                    <td style={{ padding: "6px 10px", textAlign: "right" }}>
                      <input type="number" min={0} max={r.due} step="0.01" value={r.amount === "" ? "" : r.amount} onChange={function (e) { setRowAmount(b.id, e.target.value); }} style={{ ...inp, width: 130, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, marginLeft: "auto" }} />
                    </td>
                  </tr>;
                })}</tbody>
              </table>
            </div>
          )}

          <div style={{ marginTop: 16, display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", display: "block", marginBottom: 4 }}>{t.notes}</label>
              <textarea value={notes} onChange={function (e) { setNotes(e.target.value); }} rows={2} style={{ ...inp, fontFamily: "inherit", resize: "vertical" }} />
            </div>
            <div style={{ background: "var(--color-bg)", borderRadius: 10, padding: 14, alignSelf: "end" }}>
              <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-muted)", marginBottom: 4 }}>{t.totalPayment}</div>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 22, fontWeight: 800, color: "var(--color-accent)" }}>{fmtMoney(total)}</div>
            </div>
          </div>

          {error && <div style={{ marginTop: 12, padding: "8px 12px", background: "rgba(231,76,60,0.08)", border: "1px solid rgba(231,76,60,0.25)", borderRadius: 6, color: "#DC2626", fontSize: 12, fontWeight: 600 }}>{error}</div>}
        </div>

        <div style={{ padding: "14px 24px", borderTop: "1px solid var(--color-border)", display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onCancel} style={{ padding: "8px 16px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 13, fontWeight: 500 }}>{t.cancel}</button>
          <button onClick={handleConfirm} disabled={openRows.length === 0 || total <= 0} style={{ padding: "8px 20px", borderRadius: 6, border: "none", background: (openRows.length === 0 || total <= 0) ? "var(--color-border)" : "var(--color-accent)", color: "var(--color-accent-text)", cursor: (openRows.length === 0 || total <= 0) ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 700 }}>{t.makePayments}</button>
        </div>
    </DraggableModal>
  );
}

/* ======================== MAIN REGISTRY ======================== */
export default function ExpenseBillRegistry({ lang: globalLang }) {
  var lang = globalLang || "es"; var t = T[lang];
  var [bills, setBills] = useState([]);
  var [search, setSearch] = useState(""); var [filterVendor, setFilterVendor] = useState(""); var [filterStatus, setFilterStatus] = useState("");
  /* Season filter — multi-select grouped per CLAUDE.md "REGLA DE FILTRO
     DE SEASON". Pre-selects all Open seasons on mount. */
  var [filterSeasons, setFilterSeasons] = useState(function () { return defaultOpenSeasonIds(); });
  var [dateFrom, setDateFrom] = useState(""); var [dateTo, setDateTo] = useState("");
  /* Brushed Steel redesign (ported from ShipmentRegistry.js/
     BillRegistry.js/InvoiceRegistry.js) — the always-visible filter
     row collapses into an anchored Filters popover (Vendor/Season/
     From/To), and the document/export actions (Expense Bill PDF /
     Export Excel) consolidate into one kebab menu. */
  var [showFilters, setShowFilters] = useState(false);
  var filtersRef = useRef(null);
  var [showMoreMenu, setShowMoreMenu] = useState(false);
  var moreMenuRef = useRef(null);
  useEffect(function() {
    if (!showFilters) return;
    var onDown = function(e) { if (filtersRef.current && !filtersRef.current.contains(e.target)) setShowFilters(false); };
    var onKey  = function(e) { if (e.key === "Escape") setShowFilters(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return function() {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [showFilters]);
  /* Close the "⋮" more-actions menu on outside click + Escape — same
     pattern as the filter popover right above. */
  useEffect(function() {
    if (!showMoreMenu) return;
    var onDown = function(e) { if (moreMenuRef.current && !moreMenuRef.current.contains(e.target)) setShowMoreMenu(false); };
    var onKey  = function(e) { if (e.key === "Escape") setShowMoreMenu(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return function() {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [showMoreMenu]);
  /* Local season-name helper — same shape used in BillRegistry/InvoiceRegistry.
     Uses the bill's date (or stored seasonId fallback) to find the matching
     season in gfi-seasons by date range. Emits "YYYY-YY" matching the
     format produced by seasonFilter.js. */
  var seasons = useMemo(function () { return loadLS("gfi-seasons", []); }, []);
  var seasonNameForExpenseBill = useCallback(function (date, sid) {
    if (sid) { var byId = seasons.find(function (s) { return String(s.id) === String(sid); }); if (byId) return byId.seasonName || byId.startDate.substring(0, 4) + "-" + byId.endEstimate.substring(2, 4); }
    if (!date) return "";
    var d = new Date(date + "T00:00:00");
    var s = seasons.find(function (s) { return new Date(s.startDate + "T00:00:00") <= d && d <= new Date(s.endEstimate + "T00:00:00"); });
    return s ? (s.seasonName || s.startDate.substring(0, 4) + "-" + s.endEstimate.substring(2, 4)) : "";
  }, [seasons]);
  var [sortCol, setSortCol] = useState(function() { try { var s = JSON.parse(localStorage.getItem("gfi-expense-bills-sort") || "null"); return (s && s.column) || "date"; } catch { return "date"; } });
  var [sortDir, setSortDir] = useState(function() { try { var s = JSON.parse(localStorage.getItem("gfi-expense-bills-sort") || "null"); return (s && s.direction) || "desc"; } catch { return "desc"; } });
  useEffect(function() { try { localStorage.setItem("gfi-expense-bills-sort", JSON.stringify({ column: sortCol, direction: sortDir })); } catch {} }, [sortCol, sortDir]);
  var [editing, setEditing] = useState(null); var [showForm, setShowForm] = useState(false);
  /* 2026-10-16 — view-mode panel for an EXISTING record (REGLA DE
     VENTANAS: single window, view+edit toggle inside). A brand-new
     record still goes through the standalone editing/showForm form
     above — nothing to "view" until it's been saved once. */
  var [viewing, setViewing] = useState(null);
  var [toast, setToast] = useState(null); var [loading, setLoading] = useState(true);
  var [selected, setSelected] = useState({});
  var [showBatchPay, setShowBatchPay] = useState(false);
  var [confirmationPayment, setConfirmationPayment] = useState(null);
  var [lockedRecord, setLockedRecord] = useState(null);

  var [error, setError] = useState(null);
  var [saving, setSaving] = useState(false);
  var aliveRef = useRef(true);
  var billsRef = useRef([]);
  useEffect(function() { billsRef.current = bills; }, [bills]);

  /* Source of truth = Supabase `expense_bills`. supabaseDB caches with
     a 60 s TTL so navigating in/out is instant after first load. */
  var reload = async function () {
    setError(null); setLoading(true);
    try {
      var rows = await loadFromSupabase("expense_bills", { force: true });
      if (aliveRef.current) setBills(rows);
    } catch (e) {
      if (aliveRef.current) setError("Could not load expense bills: " + (e.message || String(e)));
    } finally {
      if (aliveRef.current) setLoading(false);
    }
  };
  useEffect(function() {
    aliveRef.current = true;
    setError(null); setLoading(true);
    loadFromSupabase("expense_bills")
      .then(function (rows) { if (aliveRef.current) setBills(rows); })
      .catch(function (e) { if (aliveRef.current) setError("Could not load expense bills: " + (e.message || String(e))); })
      .finally(function () { if (aliveRef.current) setLoading(false); });
    return function () { aliveRef.current = false; };
  }, []);

  /* 2026-09-28 (Arturo: "los expense bills necesito poder editarlos
     aunque ya esten pagados para poder cambiar los conceptos del
     gasto") — always enter edit mode, even with payments applied.
     Balance recalculates automatically from the updated total minus
     existing payments (see calcBal/getStatus above), same fix already
     applied to Shipment Bills (BillRegistry.js's tryEditMode). The
     EditLockDialog/setLockedRecord machinery is left wired below in
     case a future need for a hard lock comes back, just no longer
     triggered from here. */
  var tryEdit = function(b) {
    /* Read fresh from current state via ref. Opens in VIEW mode now
       (REGLA DE VENTANAS) — the panel's own Edit button switches into
       the embedded form without a second window. */
    var fresh = (billsRef.current || []).find(function(x) { return x.id === b.id; }) || b;
    setViewing(fresh);
  };

  /* Open-by-id hook for global search — opens the view panel */
  useEffect(function() {
    window.__gfiExpenseBillOpen = function(id) {
      var b = (billsRef.current || []).find(function(x) { return x.id === id; });
      if (b) setViewing(b);
    };
    return function() { delete window.__gfiExpenseBillOpen; };
  }, []);

  /* Blank-form create hook for Create New menu */
  useEffect(function() {
    window.__gfiExpenseBillNew = function() { setEditing(null); setShowForm(true); };
    return function() { delete window.__gfiExpenseBillNew; };
  }, []);
  var handleSave = async function(b) {
    if (saving) return;
    setSaving(true); setError(null);
    try {
      var saved = await saveToSupabase("expense_bills", b);
      var current = billsRef.current || [];
      var ex = current.find(function(x) { return x.id === b.id; });
      setBills(ex ? current.map(function(x) { return x.id === b.id ? saved : x; }) : current.concat([saved]));
      setShowForm(false); setEditing(null); setToast(t.saved);
    } catch (e) {
      setError("Could not save expense bill: " + (e.message || String(e)));
      setToast("\u26a0 " + e.message);
    } finally { setSaving(false); }
  };
  var handleDelete = async function(id) {
    if (saving) return;
    setSaving(true); setError(null);
    try {
      await deleteFromSupabase("expense_bills", id);
      setBills(function (prev) { return prev.filter(function (x) { return x.id !== id; }); });
      setShowForm(false); setEditing(null); setViewing(null); setToast(t.deleted);
    } catch (e) {
      setError("Could not delete expense bill: " + (e.message || String(e)));
      setToast("\u26a0 " + e.message);
    } finally { setSaving(false); }
  };

  var uniqueVendors = useMemo(function() { return [...new Set(bills.map(function(b) { return b.vendor; }).filter(Boolean))].sort(); }, [bills]);

  /* Arturo, 2026-09-16: "PERO DEBE MOSTRAR TAMBIEN FACTURAS NO PAGADAS
     AUNQUE LA TEMPORADA ESTE CERRADA" — an unpaid bill doesn't stop
     being owed just because its season closed (same "all-time debt
     visibility" exception already applied to the Dashboard's
     InvoicesOwed/BillsToPay cards). Bills that are draft/awaiting
     (getStatus() !== "paid") always pass through regardless of the
     season filter; only paid bills respect it. */
  var seasonFilteredBills = useMemo(function() {
    if (filterSeasons.length === 0) return bills;
    return bills.filter(function(x) {
      if (getStatus(x) !== "paid") return true;
      return filterSeasons.indexOf(resolveSeasonId(seasons, x.date, x.seasonId)) >= 0;
    });
  }, [bills, filterSeasons, seasons]);

  var filtered = useMemo(function() {
    var list = seasonFilteredBills;
    if (search) { var s = search.toLowerCase(); list = list.filter(function(x) { return (x.vendor || "").toLowerCase().includes(s) || (x.reference || "").toLowerCase().includes(s); }); }
    if (filterVendor) list = list.filter(function(x) { return x.vendor === filterVendor; });
    if (filterStatus) list = list.filter(function(x) { return getStatus(x) === filterStatus; });
    if (dateFrom || dateTo) list = list.filter(function(x) { var d = x.date || ""; if (!d) return false; if (dateFrom && d < dateFrom) return false; if (dateTo && d > dateTo) return false; return true; });
    return list;
  }, [seasonFilteredBills, search, filterVendor, filterStatus, dateFrom, dateTo]);

  var sorted = useMemo(function() {
    return [...filtered].sort(function(a, b) {
      var va, vb;
      switch (sortCol) {
        case "vendor": va = a.vendor || ""; vb = b.vendor || ""; break;
        case "reference": va = a.reference || ""; vb = b.reference || ""; break;
        case "date": va = a.date || ""; vb = b.date || ""; break;
        case "dueDate": va = a.dueDate || ""; vb = b.dueDate || ""; break;
        case "amount": va = parseFloat(a.amount) || 0; vb = parseFloat(b.amount) || 0; return sortDir === "asc" ? va - vb : vb - va;
        case "paid": va = calcBal(a).paid; vb = calcBal(b).paid; return sortDir === "asc" ? va - vb : vb - va;
        case "balance": va = calcBal(a).balance; vb = calcBal(b).balance; return sortDir === "asc" ? va - vb : vb - va;
        default: va = a[sortCol] || ""; vb = b[sortCol] || "";
      }
      if (typeof va === "string") return sortDir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
      return sortDir === "asc" ? va - vb : vb - va;
    });
  }, [filtered, sortCol, sortDir]);

  var toggleSort = function(col) { if (sortCol === col) setSortDir(function(d) { return d === "asc" ? "desc" : "asc"; }); else { setSortCol(col); setSortDir("asc"); } };
  var arrow = function(col) { return sortCol === col ? (sortDir === "asc" ? " \u25b2" : " \u25bc") : ""; };

  var allSelected = sorted.length > 0 && sorted.every(function(b) { return selected[b.id]; });
  var toggleAll = function() { var ns = {}; if (!allSelected) sorted.forEach(function(b) { ns[b.id] = true; }); setSelected(ns); };
  var toggleOne = function(id) { setSelected(function(prev) { var ns = { ...prev }; if (ns[id]) delete ns[id]; else ns[id] = true; return ns; }); };
  var selectedCount = Object.keys(selected).length;

  var summary = useMemo(function() {
    var r = { all: 0, allAmt: 0, draft: 0, awaiting: 0, awaitingAmt: 0, paid: 0 };
    seasonFilteredBills.forEach(function(b) {
      var st = getStatus(b); var bal = calcBal(b);
      r.all++; r.allAmt += bal.total;
      if (st === "draft") r.draft++;
      else if (st === "awaiting") { r.awaiting++; r.awaitingAmt += bal.balance; }
      else if (st === "paid") r.paid++;
    });
    return r;
  }, [seasonFilteredBills]);

  var selectedBillsList = useMemo(function() { return bills.filter(function(b) { return selected[b.id]; }); }, [bills, selected]);
  var [showBulkDelete, setShowBulkDelete] = useState(false);
  var [showBulkStatus, setShowBulkStatus] = useState(false);
  var bulkLocked = useMemo(function() {
    return selectedBillsList.filter(function(b) { return (b.payments || []).length > 0; }).map(function(b) { return { id: b.id, label: (b.id || "—") + " · " + (b.vendor || "") }; });
  }, [selectedBillsList]);
  /* Bulk status change. Records with payments can only move to "paid". */
  var handleBulkStatusChange = async function(newStatus) {
    if (saving) return;
    setSaving(true); setError(null);
    var lockedIds = {};
    if (newStatus !== "paid") bulkLocked.forEach(function(l) { lockedIds[l.id] = true; });
    var toUpdate = bills.filter(function(b) { return selected[b.id] && !lockedIds[b.id]; });
    try {
      var savedAll = await Promise.all(toUpdate.map(function(b) { return saveToSupabase("expense_bills", { ...b, status: newStatus }); }));
      var savedMap = {};
      savedAll.forEach(function (s) { savedMap[s.id] = s; });
      setBills(bills.map(function(b) { return savedMap[b.id] || b; }));
      setShowBulkStatus(false); setSelected({}); setToast(t.saved);
    } catch (e) {
      setError("Could not bulk update: " + (e.message || String(e)));
      setToast("⚠ " + e.message);
    } finally { setSaving(false); }
  };
  var handleBulkDelete = async function() {
    if (saving) return;
    setSaving(true); setError(null);
    var lockedIds = {};
    bulkLocked.forEach(function(l) { lockedIds[l.id] = true; });
    var toDelete = bills.filter(function(b) { return selected[b.id] && !lockedIds[b.id]; });
    try {
      await Promise.all(toDelete.map(function (b) { return deleteFromSupabase("expense_bills", b.id); }));
      var deletedSet = {};
      toDelete.forEach(function (b) { deletedSet[b.id] = true; });
      setBills(bills.filter(function (b) { return !deletedSet[b.id]; }));
      setShowBulkDelete(false);
      setSelected({});
      setToast(t.deleted);
    } catch (e) {
      setError("Could not bulk delete: " + (e.message || String(e)));
      setToast("⚠ " + e.message);
    } finally { setSaving(false); }
  };
  /* Per-column breakdown for selection bar AND module-total chip:
     { amount, paid, balance }. ExpenseBills are simple — single
     amount field (sum of fee lines), payments[], balance derived. */
  function aggregateExpenseBills(list) {
    var amount = 0, paid = 0, balance = 0;
    (list || []).forEach(function(b) { var bal = calcBal(b); amount += bal.total; paid += bal.paid; balance += bal.balance; });
    return { amount: amount, paid: paid, balance: balance };
  }
  var selectedTotals = useMemo(function() { return aggregateExpenseBills(selectedBillsList); }, [selectedBillsList]);
  var selectedDueTotal = selectedTotals.balance;

  /* Excel export. ExpenseBills store one fee per "line" in bill.fees,
     each fee optionally linked to a Shipment lot and to a Bill. The
     Summary export collapses to one row per expense bill (totals).
     Detail expands one row per fee — the natural grain. */
  var expenseBillsToExport = function () { return selectedBillsList.length > 0 ? selectedBillsList : sorted; };
  var buildExpSummaryRows = function () {
    return expenseBillsToExport().map(function (b) {
      var bal = calcBal(b);
      return {
        ID: xs(b.id),
        Date: xs(b.date),
        Vendor: xs(b.vendor),
        Reference: xs(b.reference),
        Category: xs(b.category),
        "Entry #": xs(b.entryNumber),
        "Due Date": xs(b.dueDate),
        Amount: num2(bal.total),
        Paid: num2(bal.paid),
        Balance: num2(bal.balance),
        Status: xs(getStatus(b)),
      };
    });
  };
  var buildExpDetailRows = function () {
    var out = [];
    /* `shipments` is only in scope inside ExpenseForm, not in the
       registry component. Load fresh from LS for the lot lookup. */
    var allShps = getShipments();
    expenseBillsToExport().forEach(function (b) {
      var meta = { ID: xs(b.id), Date: xs(b.date), Vendor: xs(b.vendor), Reference: xs(b.reference), Category: xs(b.category) };
      (b.fees || []).forEach(function (fe) {
        var feLot = "";
        if (fe.shipmentId) {
          var shp = allShps.find(function (s) { return s.id === fe.shipmentId; });
          feLot = shp ? (shp.lot || "") : "";
        }
        out.push({
          ...meta,
          Type: "Fee",
          "Fee Type": xs(fe.feeType === "__custom__" ? (fe.feeCustom || "Custom") : fe.feeType),
          Description: xs(fe.feeCustom || ""),
          Lot: xs(feLot),
          "Linked Bill": xs(fe.billId),
          Qty: intOr(fe.qty),
          Rate: num2(fe.rate),
          Amount: num2(fe.amount),
        });
      });
      (b.payments || []).forEach(function (p) {
        out.push({ ...meta, Type: "Payment", "Fee Type": "", Description: xs(p.reference || p.method || "Payment"), Lot: "", "Linked Bill": "", Qty: "", Rate: "", Amount: num2(p.amount) });
      });
    });
    return out;
  };
  var grandTotals = useMemo(function() { return aggregateExpenseBills(sorted); }, [sorted]);
  var grandDueTotal = grandTotals.balance;

  /* Make Payment: append to bill.payments per allocation, save each
     affected expense bill to Supabase, write the central payment
     record to Supabase `payments`. nextPaymentId still computes from
     the in-memory cache via loadLS — `payments` will migrate in GRUPO
     3, until then dual-write to localStorage too so the existing
     PaymentRegistry sync consumers keep working. */
  var handleBatchPaymentConfirm = async function(payload) {
    if (saving) return;
    setSaving(true); setError(null);
    var allPayments = loadLS("gfi-payments", []);
    var newPayId = nextPaymentId(allPayments);
    var vendorNames = [...new Set(payload.allocations.map(function(a) { return a.vendor; }).filter(Boolean))];
    var paymentRecord = {
      id: newPayId,
      paymentDate: payload.paymentDate,
      processingDate: payload.processingDate || "",
      deliveryDate: payload.deliveryDate || "",
      type: "outbound",
      paymentMethod: payload.paymentMethod,
      reference: payload.reference,
      notes: payload.notes,
      allocations: payload.allocations.map(function(a) { return { expenseBillId: a.expenseBillId, vendor: a.vendor, reference: a.reference, amount: a.amount }; }),
      shipperName: vendorNames.join(", "),
      createdAt: new Date().toISOString(),
    };
    var allocByBillId = {};
    payload.allocations.forEach(function(a) { allocByBillId[a.expenseBillId] = a.amount; });
    var toUpdate = bills.filter(function(b) { return allocByBillId[b.id]; }).map(function(b) {
      return { ...b, payments: [...(b.payments || []), {
        paymentId: newPayId,
        date: payload.paymentDate,
        amount: allocByBillId[b.id],
        method: payload.paymentMethod,
        reference: payload.reference,
      }] };
    });
    try {
      var savedAll = await Promise.all(toUpdate.map(function(b) { return saveToSupabase("expense_bills", b); }));
      /* Try to also push the central payment record to Supabase
         payments. Best-effort: if the payments table isn't seeded yet
         the row won't write but the expense_bill.payments embedded
         array still records the receipt. */
      try { await saveToSupabase("payments", paymentRecord); } catch (e) { /* swallow */ }
      /* Dual write to localStorage for PaymentRegistry which still
         reads gfi-payments sync until it's migrated in GRUPO 3. */
      saveLS("gfi-payments", [...allPayments, paymentRecord]);
      var savedMap = {};
      savedAll.forEach(function (s) { savedMap[s.id] = s; });
      setBills(bills.map(function(b) { return savedMap[b.id] || b; }));
      setSelected({});
      setShowBatchPay(false);
      setToast(t.paymentSaved + " (" + newPayId + ")");
      setConfirmationPayment(paymentRecord);
    } catch (e) {
      setError("Could not record payment: " + (e.message || String(e)));
      setToast("⚠ " + e.message);
    } finally { setSaving(false); }
  };

  if (loading) return null;
  /* Brushed Steel metal-plate spec (see App.css "BRUSHED STEEL" /
     ShipmentRegistry.js, BillRegistry.js, InvoiceRegistry.js list
     headers) — brushed-metal gradient + 1px #b9bcc0 border, embossed
     text (dark #1a2636 fill, light-below/dark-above text-shadow),
     square corners, 9px column headers. Background lives on the
     <thead><tr> itself (see thead render below) for one continuous
     bar; `th` keeps only text + the shared border. */
  var th = { padding: "7px 5px", textAlign: "left", fontWeight: 700, fontSize: 9, textTransform: "uppercase", letterSpacing: "0.05em", color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.2)", cursor: "pointer", userSelect: "none", whiteSpace: "nowrap", background: "transparent", borderTop: "1px solid #b9bcc0", borderBottom: "1px solid #b9bcc0" };

  /* Xero-style header layout — mirrors BillRegistry/InvoiceRegistry.
     Tabs replace the 4 summary cards; search lives before Filters in
     the header action group; module totals move to a tight plate. */
  var tabsCfg = [
    { key: "all",      label: lang === "es" ? "Todas" : "All", count: seasonFilteredBills.length, filterValue: "" },
    { key: "draft",    label: t.statusDraft,    count: summary.draft || 0,    filterValue: "draft" },
    { key: "awaiting", label: t.statusAwaiting, count: summary.awaiting || 0, filterValue: "awaiting" },
    { key: "paid",     label: t.statusPaid,     count: summary.paid || 0,     filterValue: "paid" },
  ];
  var activeTabKey = filterStatus || "all";
  /* Active filter count — drives the badge next to the Filters
     button. filterStatus (the tabs) is NOT counted, since the tabs
     are now the visible way to filter by status. search is also
     excluded — it lives in its own always-visible pill. */
  var activeFilterCount =
      (filterVendor ? 1 : 0)
    + (filterSeasons.length > 0 ? 1 : 0)
    + ((dateFrom || dateTo) ? 1 : 0);
  var clearAllFilters = function() {
    setSearch(""); setFilterVendor(""); setFilterSeasons([]); setDateFrom(""); setDateTo("");
  };
  /* Brushed Steel action button — same spec as ShipmentRegistry.js/
     BillRegistry.js/InvoiceRegistry.js's list toolbar (square
     corners, brushed-metal gradient, embossed text). */
  var actBtn = { padding: "5px 12px", borderRadius: 0, border: "1px solid #b9bcc0", background: "linear-gradient(180deg, #f6f7f8 0%, #d8dadd 100%)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.9), inset 0 -1px 0 rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.15)", color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.2)", cursor: "pointer", fontSize: 12, fontWeight: 600 };
  var actBtnDanger = Object.assign({}, actBtn, { borderColor: "rgba(220,38,38,0.4)", color: "#B91C1C", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.1)" });
  /* Dark-silver "title plate" variant — same gradient as the title
     bar / table header bar, used for the Filters + "⋮" triggers so
     each trigger reads as one continuous plate with the popover it
     opens. */
  var titlePlateBtn = { padding: "5px 12px", borderRadius: 0, border: "1px solid #7d838c", background: "linear-gradient(180deg, #c7cbd1 0%, #9aa0a8 100%)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), inset 0 -1px 0 rgba(0,0,0,0.15)", color: "#000000", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.25)", cursor: "pointer", fontSize: 12, fontWeight: 600 };
  var totalsLabel = { textAlign: "right", color: "var(--color-text-muted)", fontSize: 11, whiteSpace: "nowrap" };
  var totalsValue = { textAlign: "right", color: "var(--color-text)", fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" };

  return (
    <div>
      {/* HEADER ROW — title (left); search + filters + kebab + new
          (right); module totals ledger right below. Brushed Steel
          metal-plate treatment ported from ShipmentRegistry.js/
          BillRegistry.js/InvoiceRegistry.js's list header. */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6, gap: 24, flexWrap: "wrap" }}>
        {/* Title + subtitle wrapped in the same dark-silver title-bar
            plate as the table's header bar / window title bar
            (#c7cbd1→#9aa0a8), black embossed text. */}
        <div style={{ lineHeight: 1.1, padding: "8px 14px", background: "linear-gradient(180deg, #c7cbd1 0%, #9aa0a8 100%)", border: "1px solid #7d838c", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), inset 0 -1px 0 rgba(0,0,0,0.15)" }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0, color: "#000000", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.25)" }}>{t.title}</h2>
          <span style={{ fontSize: 10, color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)" }}>{t.subtitle}</span>
        </div>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 24, flexWrap: "wrap" }}>
          {/* Action group: search + filters + kebab (document/export
              actions) + New Expense Bill. */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, position: "relative" }} ref={filtersRef}>
            {/* SEARCH — neumorphic white pill with an embossed
                magnifier icon. */}
            <span style={{ position: "relative", display: "inline-block" }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round" style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", filter: "drop-shadow(0 1px 0 rgba(255,255,255,0.9)) drop-shadow(0 -1px 1px rgba(0,0,0,0.3))" }}>
                <circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                value={search}
                onChange={function(e) { setSearch(e.target.value); }}
                placeholder={t.search}
                style={{ width: 200, padding: "6px 10px 6px 28px", fontSize: 12, borderRadius: 999, border: "none", background: "#ffffff", boxShadow: "inset 1px 1px 3px rgba(0,0,0,0.15), inset -1px -1px 2px rgba(255,255,255,0.8), 0 1px 1px rgba(255,255,255,0.9), 0 -1px 1px rgba(0,0,0,0.1)", outline: "none", fontFamily: "inherit", color: "var(--color-text-secondary)" }}
              />
            </span>
            {search && (
              <button
                type="button"
                onClick={function() { setSearch(""); }}
                style={{ padding: "5px 10px", borderRadius: 5, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 11 }}
                title={lang === "es" ? "Limpiar búsqueda" : "Clear search"}
              >×</button>
            )}
            <button
              type="button"
              onClick={function() { setShowFilters(function(v) { return !v; }); }}
              style={Object.assign({}, titlePlateBtn, { display: "flex", alignItems: "center", gap: 6, padding: "5px 10px" })}
              title={lang === "es" ? "Filtros" : "Filters"}
            >
              <span style={{ fontSize: 16, lineHeight: 1, letterSpacing: 0 }}>⋮</span>
              {lang === "es" ? "Filtros" : "Filters"}
              {activeFilterCount > 0 && (
                <span style={{ minWidth: 18, height: 18, padding: "0 5px", borderRadius: 9, background: "var(--color-accent)", color: "var(--color-accent-text)", fontSize: 10, fontWeight: 700, lineHeight: "18px", textAlign: "center", marginLeft: 2 }}>{activeFilterCount}</span>
              )}
            </button>
            {/* KEBAB — document/export actions (Expense Bill PDF /
                Export Excel). These are secondary/less-frequent
                actions compared to Make Payment, which stays its own
                visible button next to Change Status. Export Excel
                embeds the real ExportButton component as its own row
                (reused as-is, never re-implemented). */}
            <span style={{ position: "relative", display: "inline-block" }} ref={moreMenuRef}>
              <button
                type="button"
                onClick={function() { setShowMoreMenu(function(v) { return !v; }); }}
                style={Object.assign({}, titlePlateBtn, { display: "flex", alignItems: "center", padding: "5px 10px" })}
                title={lang === "es" ? "Más acciones" : "More actions"}
              >
                <span style={{ fontSize: 16, lineHeight: 1, letterSpacing: 0 }}>⋮</span>
              </button>
              {showMoreMenu && (
                <div style={{ position: "absolute", top: "100%", left: 0, marginTop: 4, background: "linear-gradient(180deg, #c7cbd1 0%, #9aa0a8 100%)", border: "1px solid #7d838c", borderRadius: 0, boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), inset 0 -1px 0 rgba(0,0,0,0.15), 0 8px 24px rgba(0,0,0,0.18)", zIndex: 50, minWidth: 220, overflow: "visible", padding: 4 }}>
                  <button
                    type="button"
                    disabled={selectedCount === 0}
                    onClick={function() {
                      if (selectedBillsList.length === 0) return;
                      setShowMoreMenu(false);
                      printMultipleExpenseBills(selectedBillsList, function(err) {
                        if (err === "popup-blocked") setToast(lang === "es" ? "⚠ El navegador bloqueó la ventana. Habilita pop-ups." : "⚠ Pop-up blocked. Allow pop-ups for this site.");
                      });
                    }}
                    style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "8px 10px", border: "none", background: "transparent", textAlign: "left", cursor: selectedCount === 0 ? "not-allowed" : "pointer", opacity: selectedCount === 0 ? 0.5 : 1, fontSize: 12, color: "#000000", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)", borderRadius: 0 }}
                    onMouseEnter={function(e) { if (selectedCount > 0) e.currentTarget.style.background = "rgba(255,255,255,0.3)"; }}
                    onMouseLeave={function(e) { e.currentTarget.style.background = "transparent"; }}
                  ><span>📄</span><span>{t.expenseBillPdf}</span></button>
                  <div style={{ padding: "4px 10px" }}>
                    {/* btnStyle matches the plain menu item above
                        exactly (transparent, black embossed text, no
                        border), instead of ExportButton's own default
                        green pill — same fix as Bills'/Invoices'/
                        Shipments' kebabs. */}
                    <ExportButton lang={lang} filenameBase="ExpenseBills" selectedCount={selectedCount} totalCount={sorted.length} buildSummary={buildExpSummaryRows} buildDetail={buildExpDetailRows} style={{ width: "100%" }} btnStyle={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "8px 10px", border: "none", borderRadius: 0, background: "transparent", color: "#000000", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)", fontSize: 12, fontWeight: 400 }} />
                  </div>
                </div>
              )}
            </span>
            {/* + New Expense Bill — Brushed Steel's "colored/active
                plate" variant (same blue gradient used for Save/active
                toggles elsewhere in this design), square corners. */}
            <button onClick={function() { setEditing(null); setShowForm(true); }} style={{ padding: "6px 14px", borderRadius: 0, border: "1px solid #1a3550", background: "linear-gradient(180deg, #6FA3C7 0%, #3E6E96 55%, #284d6b 100%)", boxShadow: "inset 0 1px 1px rgba(255,255,255,0.3), inset 0 -1px 2px rgba(0,0,0,0.2), 0 1px 2px rgba(0,0,0,0.15)", color: "#ffffff", textShadow: "0 1px 1px rgba(0,0,0,0.25)", cursor: "pointer", fontSize: 12, fontWeight: 700, display: "flex", alignItems: "center", gap: 5 }}><span style={{ fontSize: 14, lineHeight: 1 }}>+</span>{t.add}</button>
            {/* FILTER POPOVER — absolute under the trigger row, same
                dark-silver plate as the title plate; fields inside
                (SearchSelect, date inputs) stay their own recessed
                plates sitting on it. */}
            {showFilters && (
              <div style={{ position: "absolute", top: "100%", right: 0, marginTop: 8, zIndex: 50, width: 440, maxWidth: "94vw", background: "linear-gradient(180deg, #c7cbd1 0%, #9aa0a8 100%)", border: "1px solid #7d838c", borderRadius: 0, boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), inset 0 -1px 0 rgba(0,0,0,0.15), 0 8px 24px rgba(0,0,0,0.18)", padding: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#000000", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)" }}>{lang === "es" ? "Filtros" : "Filters"}{activeFilterCount > 0 ? " (" + activeFilterCount + ")" : ""}</div>
                  <div style={{ display: "flex", gap: 6 }}>
                    {activeFilterCount > 0 && (
                      <button type="button" onClick={clearAllFilters} style={Object.assign({}, actBtn, { padding: "4px 8px", fontSize: 10 })}>{lang === "es" ? "Limpiar todo" : "Clear all"}</button>
                    )}
                    <button type="button" onClick={function() { setShowFilters(false); }} style={Object.assign({}, actBtn, { padding: "4px 8px", fontSize: 10 })}>{lang === "es" ? "Cerrar" : "Close"}</button>
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                  <div>
                    <div style={{ fontSize: 9, fontWeight: 700, color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 2 }}>{t.allVendors}</div>
                    <SearchSelect value={filterVendor} onChange={setFilterVendor} placeholder={t.allVendors} options={uniqueVendors.map(function(v) { return { value: v, label: v }; })} style={filterFieldPlate} />
                  </div>
                  <div>
                    <div style={{ fontSize: 9, fontWeight: 700, color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 2 }}>{lang === "es" ? "Temporada" : "Season"}</div>
                    <SearchSelect multi value={filterSeasons} onChange={setFilterSeasons} placeholder={seasonFilterPlaceholder(lang)} groups={buildSeasonFilterOptions(lang)} style={filterFieldPlate} />
                  </div>
                  <div>
                    <div style={{ fontSize: 9, fontWeight: 700, color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 2 }}>{lang === "es" ? "Desde" : "From"}</div>
                    <input type="date" value={dateFrom} onChange={function(e) { setDateFrom(e.target.value); }} style={Object.assign({}, inp, filterFieldPlate)} />
                  </div>
                  <div>
                    <div style={{ fontSize: 9, fontWeight: 700, color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 2 }}>{lang === "es" ? "Hasta" : "To"}</div>
                    <input type="date" value={dateTo} onChange={function(e) { setDateTo(e.target.value); }} style={Object.assign({}, inp, filterFieldPlate)} />
                  </div>
                </div>
              </div>
            )}
          </div>
          {/* MODULE TOTALS — same dark title-bar silver plate as the
              header bar / title plate above. None of Amount/Paid/
              Balance is semantically a deduction here (unlike Bills'
              Fees/Invoices' Deductions), so all three stay uniform
              black. */}
          <div style={{ display: "grid", gridTemplateColumns: "auto auto", columnGap: 10, rowGap: 1, alignItems: "center", alignSelf: "flex-start", padding: "4px 10px", background: "linear-gradient(180deg, #c7cbd1 0%, #9aa0a8 100%)", border: "1px solid #7d838c", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), inset 0 -1px 0 rgba(0,0,0,0.15)" }}>
            <span style={{ ...totalsLabel, fontSize: 9, color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)" }}>{lang === "es" ? "Monto" : "Amount"}:</span>
            <span style={{ ...totalsValue, fontSize: 10, color: "#000000", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.25)" }}>{fmtMoney(grandTotals.amount)}</span>
            <span style={{ ...totalsLabel, fontSize: 9, color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)" }}>{lang === "es" ? "Pagado" : "Paid"}:</span>
            <span style={{ ...totalsValue, fontSize: 10, color: "#000000", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.25)" }}>{fmtMoney(grandTotals.paid)}</span>
            <span style={{ ...totalsLabel, fontSize: 9, color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)" }}>{lang === "es" ? "Saldo" : "Balance"}:</span>
            <span style={{ ...totalsValue, fontSize: 10, color: "#000000", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.25)" }}>{fmtMoney(grandTotals.balance)}</span>
          </div>
        </div>
      </div>
      {error && <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "6px 10px", borderRadius: 8, background: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.3)", marginBottom: 6, fontSize: 12, color: "#B91C1C", fontWeight: 500 }}><span style={{ fontSize: 14 }}>⚠</span><span style={{ flex: 1, fontFamily: "'JetBrains Mono', monospace", wordBreak: "break-word" }}>{error}</span><button onClick={reload} style={{ padding: "4px 10px", borderRadius: 4, border: "1px solid #991B1B", background: "transparent", color: "#991B1B", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{lang === "es" ? "Reintentar" : "Retry"}</button></div>}
      {/* TABS — All / Draft / Awaiting Payment / Paid. Restyled as
          inset/pressed segments (no underline). Make Payment / Change
          Status / Delete Selected / Clear used to be a separate
          selection row below (along with the standalone search/filter
          row above, now relocated into the header action group) —
          both moved here, right after the Paid tab, same compact
          Brushed Steel metal plate buttons (actBtn/actBtnDanger) as
          the module totals ledger above. */}
      <div style={{ display: "flex", alignItems: "center", gap: 2, marginBottom: 6, flexWrap: "wrap" }}>
        {tabsCfg.map(function(tab) {
          var isActive = activeTabKey === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={function() { setFilterStatus(tab.filterValue); }}
              style={{
                padding: "5px 12px",
                background: isActive ? "#ffffff" : "transparent",
                boxShadow: isActive ? "inset 1px 1px 2px rgba(0,0,0,0.15)" : "none",
                border: "none",
                color: "#1a2636",
                fontWeight: 500,
                fontSize: 11,
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              {tab.label} ({tab.count})
            </button>
          );
        })}
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginLeft: 10, marginBottom: 2 }}>
          <button type="button" onClick={function() { setShowBatchPay(true); }} disabled={selectedCount === 0} style={Object.assign({}, actBtn, { padding: "4px 9px", fontSize: 11, opacity: selectedCount === 0 ? 0.5 : 1, cursor: selectedCount === 0 ? "not-allowed" : "pointer" })}>{t.makePayment}</button>
          <button type="button" onClick={function() { setShowBulkStatus(true); }} disabled={selectedCount === 0} style={Object.assign({}, actBtn, { padding: "4px 9px", fontSize: 11, opacity: selectedCount === 0 ? 0.5 : 1, cursor: selectedCount === 0 ? "not-allowed" : "pointer" })}>{lang === "es" ? "Cambiar Estado" : "Change Status"}</button>
          <button type="button" onClick={function() { setShowBulkDelete(true); }} disabled={selectedCount === 0} style={Object.assign({}, actBtnDanger, { padding: "4px 9px", fontSize: 11, opacity: selectedCount === 0 ? 0.5 : 1, cursor: selectedCount === 0 ? "not-allowed" : "pointer" })}>{lang === "es" ? "Eliminar" : "Delete Selected"}</button>
          <button type="button" onClick={function() { setSelected({}); }} disabled={selectedCount === 0} style={Object.assign({}, actBtn, { padding: "4px 9px", fontSize: 11, opacity: selectedCount === 0 ? 0.5 : 1, cursor: selectedCount === 0 ? "not-allowed" : "pointer" })}>{lang === "es" ? "Limpiar" : "Clear"}</button>
        </div>

      {selectedCount > 0 && (
        <div style={{ marginLeft: "auto", display: "flex", gap: 14, flexWrap: "wrap", fontSize: 11, alignItems: "center", marginBottom: 6 }}>
          <span style={{ fontWeight: 700, color: "var(--color-accent)" }}>{selectedCount} {t.selected}</span>
          <span style={{ color: "var(--color-text-muted)" }}>|</span>
          <span><span style={{ color: "var(--color-text-muted)" }}>{lang === "es" ? "Monto" : "Amount"}: </span><span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "var(--color-text)" }}>{fmtMoney(selectedTotals.amount)}</span></span>
          <span><span style={{ color: "var(--color-text-muted)" }}>{lang === "es" ? "Pagado" : "Paid"}: </span><span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "#059669" }}>{fmtMoney(selectedTotals.paid)}</span></span>
          <span><span style={{ color: "var(--color-text-muted)" }}>{lang === "es" ? "Saldo" : "Balance"}: </span><span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "var(--color-accent)" }}>{fmtMoney(selectedTotals.balance)}</span></span>
        </div>
      )}
      </div>

      {sorted.length === 0 ? <div style={{ textAlign: "center", padding: "40px 20px", color: "var(--color-text-muted)", fontSize: 14 }}>{t.noResults}</div> : (
        <div style={{ overflowX: "auto", borderRadius: 0, border: "1px solid #b9bcc0", background: "#ffffff" }}>
          <table className="gfi-expensebills-list-table" style={{ width: "100%", borderCollapse: "collapse", tableLayout: "auto" }}>
            <thead><tr style={{ background: "linear-gradient(180deg, #eef0f2 0%, #d2d5d9 100%)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.9), inset 0 -1px 0 rgba(0,0,0,0.08)" }}>
              <th style={{ ...th, width: 30, cursor: "default" }}><input type="checkbox" checked={allSelected} onChange={toggleAll} /></th>
              <th onClick={function() { toggleSort("vendor"); }} style={th}>{t.vendor}{arrow("vendor")}</th>
              <th onClick={function() { toggleSort("reference"); }} style={th}>{t.reference}{arrow("reference")}</th>
              <th onClick={function() { toggleSort("date"); }} style={th}>{t.date}{arrow("date")}</th>
              <th onClick={function() { toggleSort("dueDate"); }} style={th}>{t.dueDate}{arrow("dueDate")}</th>
              <th onClick={function() { toggleSort("amount"); }} style={{ ...th, textAlign: "right" }}>{t.amount}{arrow("amount")}</th>
              <th onClick={function() { toggleSort("paid"); }} style={{ ...th, textAlign: "right" }}>{t.paid}{arrow("paid")}</th>
              <th onClick={function() { toggleSort("balance"); }} style={{ ...th, textAlign: "right" }}>{t.due}{arrow("balance")}</th>
              {/* Documents — presence icon, centered reference-type
                  column, not a text column. */}
              <th style={{ ...th, textAlign: "center", cursor: "default" }}>📎</th>
              <th style={{ ...th, textAlign: "center" }}>{t.status}</th>
              <th style={{ ...th, textAlign: "center" }}>{t.action}</th>
            </tr></thead>
            <tbody>{sorted.map(function(b) {
              var bal = calcBal(b); var st = getStatus(b); var ss = STATUS_STYLES[st] || STATUS_STYLES.draft;
              /* 2026-10-16, Arturo: "los expenses tampoco se abren...
                 al dar click debe abrirlo en modo de ver con boton para
                 editar tal como cualquier invoice u otro" \u2014 this row
                 never had an onClick at all, the tiny "Edit" button
                 (which already opens VIEW mode, see tryEdit) was the
                 ONLY way in. Same full-row-click treatment as Payment
                 Registry/Bill: the <tr> plus every plain <td> now opens
                 it, matching every other registry in the app. */
              var openRow = function() { tryEdit(b); };
              return <tr key={b.id} onClick={openRow} style={{ cursor: "pointer", transition: "background 0.15s", borderBottom: "1px solid var(--color-border)", background: selected[b.id] ? "rgba(185,188,192,0.3)" : "transparent" }} onMouseEnter={function(e) { e.currentTarget.style.background = selected[b.id] ? "rgba(185,188,192,0.4)" : "rgba(185,188,192,0.2)"; }} onMouseLeave={function(e) { e.currentTarget.style.background = selected[b.id] ? "rgba(185,188,192,0.3)" : "transparent"; }}>
                <td style={{ padding: "6px 8px" }} onClick={function(e) { e.stopPropagation(); }}><input type="checkbox" checked={!!selected[b.id]} onChange={function() { toggleOne(b.id); }} /></td>
                <td onClick={openRow} style={{ padding: "6px 8px", fontSize: 9.5, whiteSpace: "nowrap" }}>{b.vendor || "\u2014"}</td>
                <td onClick={openRow} style={{ padding: "6px 8px", fontSize: 9.5, color: "var(--color-text-secondary)", whiteSpace: "nowrap" }}>{b.reference || "\u2014"}</td>
                <td onClick={openRow} style={{ padding: "6px 8px", fontSize: 9.5, color: "var(--color-text-secondary)" }}>{fmtDate(b.date)}</td>
                <td onClick={openRow} style={{ padding: "6px 8px", fontSize: 9.5, color: st === "overdue" ? "#DC2626" : "var(--color-text-secondary)", fontWeight: st === "overdue" ? 600 : 400 }}>{fmtDate(b.dueDate)}</td>
                <td onClick={openRow} style={{ padding: "6px 8px", textAlign: "right", fontSize: 9.5 }}>{fmtMoney(bal.total)}</td>
                <td onClick={openRow} style={{ padding: "6px 8px", textAlign: "right", fontSize: 9.5, color: bal.paid > 0 ? "#059669" : "var(--color-text-muted)" }}>{bal.paid > 0 ? fmtMoney(bal.paid) : "\u2014"}</td>
                <td onClick={openRow} style={{ padding: "6px 8px", textAlign: "right", fontSize: 9.5, fontWeight: 600, color: bal.balance > 0 ? "#DC2626" : "#059669" }}>{fmtMoney(bal.balance)}</td>
                <td onClick={openRow} style={{ padding: "6px 8px", textAlign: "center" }}>{b.documents && b.documents.length > 0 ? <a href={b.documents[0].url} target="_blank" rel="noopener noreferrer" title={b.documents.map(function(d) { return d.name; }).join(", ")} onClick={function(e) { e.stopPropagation(); }} style={{ fontSize: 13, textDecoration: "none", color: "var(--color-text)", fontWeight: 600 }}>📎{b.documents.length > 1 ? " " + b.documents.length : ""}</a> : null}</td>
                <td onClick={openRow} style={{ padding: "6px 8px", textAlign: "center" }}><span style={{ padding: "3px 9px", borderRadius: 0, fontSize: 9.5, fontWeight: 400, background: ss.bg, color: ss.text, border: "1px solid " + ss.border, boxShadow: STATUS_BEVEL }}>{t[stKeys[st]]}</span></td>
                <td style={{ padding: "6px 4px", textAlign: "center" }} onClick={function(e) { e.stopPropagation(); }}><button onClick={openRow} style={{ padding: "4px 10px", borderRadius: 5, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-accent)", cursor: "pointer", fontSize: 10, fontWeight: 600 }}>{t.editBtn}</button></td>
              </tr>;
            })}</tbody>
          </table>
        </div>
      )}
      {showForm && <ExpenseForm bill={editing} bills={bills} t={t} lang={lang} onSave={handleSave} onDelete={handleDelete} onCancel={function() { setShowForm(false); setEditing(null); }} />}
      {viewing && <ExpenseBillDetailPanel
        bill={viewing}
        bills={bills}
        t={t} lang={lang}
        onSave={function(updated) { handleSave(updated); setViewing(updated); }}
        onDelete={handleDelete}
        onClose={function() { setViewing(null); }}
      />}
      {showBatchPay && <BatchPaymentModal bills={selectedBillsList} t={t} lang={lang} onConfirm={handleBatchPaymentConfirm} onCancel={function() { setShowBatchPay(false); }} />}
      {showBulkDelete && <BulkDeleteDialog count={selectedCount} lockedRecords={bulkLocked} lang={lang} onCancel={function() { setShowBulkDelete(false); }} onConfirm={handleBulkDelete} />}
      {showBulkStatus && <BulkStatusDialog count={selectedCount} lockedRecords={bulkLocked} statuses={["draft", "awaiting", "paid"].map(function(s) { return { value: s, label: t[stKeys[s]] || s }; })} lang={lang} onCancel={function() { setShowBulkStatus(false); }} onConfirm={handleBulkStatusChange} />}
      {confirmationPayment && (function() { var pr = partyAndRowsForExpensePayment(confirmationPayment); return <PaymentConfirmationPreview payment={confirmationPayment} party={pr.party} rows={pr.rows} lang={lang} onClose={function() { setConfirmationPayment(null); }} />; })()}
      {lockedRecord && <EditLockDialog record={lockedRecord} t={t} onClose={function() { setLockedRecord(null); }} />}
      {toast && <Toast message={toast} onClose={function() { setToast(null); }} />}
    </div>
  );
}